import { Activity, Building2, Check, CheckCircle2, ChevronDown, Download, FileText, Globe2, KeyRound, LayoutGrid, ListChecks, Mail, MoreHorizontal, Pause, Pencil, Plus, Power, PowerOff, Search, Send, Settings, ShieldCheck, Trash2, UserPlus, UserRound, UsersRound, X } from "lucide-react";
import { useAction, useConvex, useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ActionModal } from "../components/ActionModal";
import { AdminMissionDashboard } from "../components/AdminMissionDashboard";
import { JurisdictionSelect } from "../components/JurisdictionSelect";
import { PublicSiteEditorPanel } from "../components/PublicSiteEditorPanel";
import { Avatar, Button, Card, Label, PageHeader, Pill, Section, Tag, type PillTone } from "../components/ui";
import { convexApi } from "../convexApi";
import { confidentialityLevels, jurisdictionTags, postTypes, topicTags } from "../lib/classification";
import { canReviewPublicSiteChanges, canUsePublicSiteEditor, canViewAdmin, hasTechnicalRole, isAdmin, memberCategoryForUser, memberCategoryLabel, technicalRoleLabel, technicalRoles, userMemberCategories } from "../lib/domain";
import { lexmosaicPracticeAreaCatalog, type PracticeAreaDefinition } from "../lib/practiceAreas";
import type { AffiliationGroup, ConfidentialityLevel, Firm, LexData, MembershipCategory, RepresentativeType, Role, TechnicalRole, TopicTag, User, UserMemberCategory } from "../lib/types";

const sections = ["Mission KPIs", "Firms and users", "Registered accounts", "Membership applications", "Public site", "Legislative sources", "Practice areas", "Post classification", "Membership categories", "Affiliation groups", "Governance settings", "Reports and exports", "Audit log", "System health"];
const reportTypes = ["Member list", "Practice areas", "Participation points", "Voting records", "Procurations", "Cooperation events", "Cooperation bands", "Legislation projects", "Outreach log", "Audit log"];
const representativeTypes: RepresentativeType[] = ["principal", "alternate", "participant", "none"];
const membershipCategories: MembershipCategory[] = ["Full Member", "Associate Member", "Expert Contributor", "Observer"];
const affiliationTypes = ["common ownership", "common control", "common brand", "formal alliance", "common management", "strategic partnership", "other structural relationship"];

type MembershipCategoryConfig = {
  category: MembershipCategory;
  explanation: string;
  accessSummary: string;
  votingSummary: string;
  active: boolean;
};

type ClerkAccount = {
  clerkId: string;
  email: string;
  name: string;
  createdAt: number;
  lastSignInAt: number | null;
  provisioned: boolean;
  convexUserId: string | null;
  memberCategory: string | null;
  role: string | null;
  hasFirm: boolean;
  active: boolean | null;
};

type MembershipApplicationStatus = "new" | "in_review" | "follow_up" | "invited" | "declined" | "archived";

type MembershipApplication = {
  _id: string;
  firmName: string;
  jurisdiction: string;
  websiteUrl?: string | null;
  lawyerCount: number;
  contactName: string;
  contactEmail: string;
  roleTitle: string;
  membershipInterest: MembershipCategory;
  independenceStatement: string;
  practiceFocus: string;
  message?: string | null;
  status: MembershipApplicationStatus;
  adminNote?: string | null;
  linkedProposalId?: string | null;
  admittedFirmId?: string | null;
  createdAt: number;
  updatedAt: number;
};

type ReportRequest = {
  type: string;
  from: string;
  to: string;
  format: string;
};

type GovernanceSettings = {
  baseFirmWeight: number;
  maxFirmWeight: number;
  quorumThreshold: number;
  majorityThreshold: number;
  procurationCap: number;
  sensitiveDecisionRule: string;
  secretBallotDefault: boolean;
  participationWeightingEnabled: boolean;
};

type AuditRecord = {
  id: string;
  actionType: string;
  entityType: string;
  entityId: string;
  actor?: string;
  actorUserId?: string | null;
  createdAt: number;
  beforeJson?: string | null;
  afterJson?: string | null;
};

type AdminSettingRecord = {
  id: string;
  key: string;
  valueJson: string;
  updatedAt: number;
};

type AdminInvitationRecord = {
  id: string;
  userId: string;
  email: string;
  status: "not_invited" | "invited" | "resent" | "accepted" | "revoked";
  sentAt: number;
  updatedAt: number;
};

type AdminEmailOutboxRecord = {
  id: string;
  userId: string;
  toEmail: string;
  subject: string;
  targetType: string;
  targetId: string;
  status: "queued" | "sent" | "failed";
  error?: string | null;
  queuedAt: number;
  sentAt?: number | null;
  updatedAt: number;
};

type AdminReportResult = {
  id: string;
  filename: string;
  mimeType: string;
  content: string;
};

type AdminOverview = {
  auditLogs: AuditRecord[];
  adminSettings: AdminSettingRecord[];
  reports: { id: string; type: string; resultJson?: string; createdAt: number }[];
  invitations: AdminInvitationRecord[];
  emailOutbox?: AdminEmailOutboxRecord[];
};

type PublicContentItem = {
  id: string;
  itemKey: string;
  contentType: "Page" | "News" | "Current action";
  title: string;
  slug: string;
  path: string;
  summary: string;
  body: string;
  imageUrl?: string | null;
  status: "published" | "draft" | "pending approval";
  updatedAt: number;
  tags?: string[];
  publicDate?: string | null;
  actionStatus?: string | null;
  lastPublishedAt?: number | null;
  publishError?: string | null;
};

type PublicContentChange = {
  id: string;
  itemKey: string;
  contentType: "Page" | "News" | "Current action";
  title: string;
  path: string;
  submittedBy: string;
  submittedAt: number;
  reviewedAt?: number | null;
  status: "pending" | "approved" | "rejected";
  note: string;
  beforeJson: string;
  afterJson: string;
};

type PublicMediaAsset = {
  id: string;
  label: string;
  url: string;
  alt: string;
};

type PublicSiteAdminData = {
  items: PublicContentItem[];
  changes: PublicContentChange[];
  media: PublicMediaAsset[];
};

const defaultGovernanceSettings: GovernanceSettings = {
  baseFirmWeight: 1,
  maxFirmWeight: 1.5,
  quorumThreshold: 0.55,
  majorityThreshold: 0.5,
  procurationCap: 2,
  sensitiveDecisionRule: "Sensitive decisions use one firm-level vote and require Super Admin certification.",
  secretBallotDefault: false,
  participationWeightingEnabled: true,
};

const defaultMembershipConfigs: MembershipCategoryConfig[] = [
  {
    category: "Full Member",
    explanation: "Independent member firm or sole practitioner admitted as a full institutional member of Lexmosaic.",
    accessSummary: "Full member network access, discussions, member directory, legislation work and governance workflows.",
    votingSummary: "Full binding voting rights for eligible decisions, with firm-level voting weight and procurations where permitted.",
    active: true,
  },
  {
    category: "Associate Member",
    explanation: "A participating firm or lawyer with limited network participation, often before or outside full membership.",
    accessSummary: "Member-facing access where invited, including discussions, selected projects and directory visibility.",
    votingSummary: "Limited voting weight only for decision types where Associate voting is enabled; advisory poll otherwise.",
    active: true,
  },
  {
    category: "Expert Contributor",
    explanation: "A specialist contributor invited for expertise in a project, practice area or working group.",
    accessSummary: "Access is linked to relevant discussions, legislation files or working groups.",
    votingSummary: "Limited voting weight only where expert participation is expressly allowed; no Full Member quorum effect.",
    active: true,
  },
  {
    category: "Observer",
    explanation: "A read-limited participant invited to follow selected network activity without membership voting rights.",
    accessSummary: "Restricted read access only to specified materials.",
    votingSummary: "No binding voting rights. May be invited to advisory polls only.",
    active: true,
  },
];

type AdminModal =
  | { kind: "firm"; firm?: Firm }
  | { kind: "user"; user?: User; firmId?: string }
  | { kind: "resetMfa"; user: User }
  | { kind: "suspendUser"; user: User }
  | { kind: "removeUser"; user: User }
  | { kind: "suspendFirm"; firm: Firm; userCount: number }
  | { kind: "removeFirm"; firm: Firm; userCount: number }
  | { kind: "practice"; area?: LexData["practiceAreas"][number]; title?: string }
  | { kind: "topicMerge"; sourceName: string }
  | { kind: "topicTag"; value?: string }
  | { kind: "jurisdictionTag"; value?: string }
  | { kind: "membership"; config: MembershipCategoryConfig }
  | { kind: "affiliation"; group?: AffiliationGroup; title?: string }
  | { kind: "audit"; record: AuditRecord }
  | { kind: "userActivity"; user: User }
  | { kind: "health"; title?: string };

type UserActivity = {
  lastSeenAt: number | null;
  name: string;
  email: string;
  auditLogs: Array<{ id: string; actionType: string; entityType: string; entityId: string; createdAt: number }>;
  activityEvents: Array<{ id: string; type: string; title: string; summary: string; targetType: string; createdAt: number }>;
};

type AdminPersistence = {
  generateReport?: (request: ReportRequest) => Promise<AdminReportResult>;
  updateFirm?: (firm: Firm) => Promise<unknown>;
  upsertFirm?: (firm: Firm) => Promise<unknown>;
  suspendFirm?: (firmId: string, reason?: string) => Promise<unknown>;
  removeFirm?: (firmId: string, reason?: string) => Promise<unknown>;
  upsertUser?: (user: User) => Promise<unknown>;
  generateProfilePhotoUploadUrl?: () => Promise<string>;
  loadUserActivity?: (userId: string) => Promise<UserActivity>;
  suspendUser?: (userId: string, reason?: string) => Promise<unknown>;
  removeUser?: (userId: string, reason?: string) => Promise<unknown>;
  resetUserMfa?: (userId: string, reason?: string) => Promise<unknown>;
  sendUserInvitation?: (userId: string) => Promise<InvitationEmailResult>;
  setFirmGovernanceAndPractices?: (input: { firmId: string; votingRepresentativeUserId?: string | null; alternateRepresentativeUserIds: string[]; practiceAreaIds: string[] }) => Promise<unknown>;
  upsertPracticeArea?: (area: LexData["practiceAreas"][number]) => Promise<unknown>;
  createPracticeArea?: (area: { name: string; slug: string; description: string }) => Promise<unknown>;
  setPracticeAreaActive?: (areaId: string, active: boolean) => Promise<unknown>;
  upsertMembershipCategoryConfig?: (config: MembershipCategoryConfig) => Promise<unknown>;
  upsertAffiliationGroup?: (group: AffiliationGroup) => Promise<unknown>;
  upsertTopicTag?: (name: string) => Promise<unknown>;
  setTopicTagStatus?: (name: string, status: "approved" | "archived") => Promise<unknown>;
  mergeTopicTag?: (sourceName: string, targetName: string) => Promise<unknown>;
  createJurisdictionTag?: (name: string) => Promise<unknown>;
  setJurisdictionTagActive?: (name: string, active: boolean) => Promise<unknown>;
  setDefaultConfidentiality?: (value: ConfidentialityLevel) => Promise<unknown>;
  saveGovernanceSettings?: (settings: GovernanceSettings) => Promise<unknown>;
  backfillUserMemberAccess?: () => Promise<{ updated: number }>;
  saveAdminNote?: (input: { kind: string; title: string; notes: string }) => Promise<unknown>;
  reviewMembershipApplication?: (input: { applicationId: string; status: MembershipApplicationStatus; adminNote?: string }) => Promise<InvitationEmailResult | unknown>;
  proposeAdmission?: (input: { applicationId: string }) => Promise<{ proposalId: string }>;
  finalizeAdmission?: (input: { applicationId: string }) => Promise<{ firmId: string }>;
  listClerkAccounts?: () => Promise<ClerkAccount[]>;
  provisionAccount?: (input: { clerkUserId: string; email: string; name: string; memberCategory?: string }) => Promise<{ status: string; userId: string }>;
  upsertPublicContentDraft?: (item: PublicContentItem) => Promise<unknown>;
  submitPublicContentChange?: (item: PublicContentItem, note?: string) => Promise<unknown>;
  reviewPublicContentChange?: (changeId: string, status: "approved" | "rejected") => Promise<unknown>;
  addPublicMediaAsset?: (asset: { label: string; url: string; alt: string }) => Promise<unknown>;
  retryPublicContentPublish?: (itemKey: string) => Promise<unknown>;
  setLegislativeSourceActive?: (input: { sourceDefinitionId: string; active: boolean }) => Promise<unknown>;
  updateLegislativeSource?: (input: { sourceDefinitionId: string; label?: string; url?: string }) => Promise<unknown>;
  deleteLegislativeSource?: (input: { sourceDefinitionId: string }) => Promise<unknown>;
  reviewLegislativeSnapshot?: (input: { reviewId: string; status: "approved" | "rejected"; note?: string }) => Promise<unknown>;
  refreshLegislativeJurisdiction?: (jurisdiction: string) => Promise<unknown>;
  suggestLegislativeSource?: (input: { url: string; label?: string }) => Promise<{ accepted: boolean; summary: string }>;
};

type InvitationEmailResult = {
  emailSent?: boolean;
  emailError?: string;
  email?: string;
  contactEmail?: string;
};

type AdminView = "home" | "public-site" | "reports";

export function AdminPage({ data, view, mode = "demo" }: { data: LexData; view: AdminView; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveAdminPage data={data} view={view} />;
  return <AdminPageContent data={data} view={view} applications={[]} />;
}

function LiveAdminPage({ data, view }: { data: LexData; view: AdminView }) {
  const fullAdmin = canViewAdmin(data.viewer);
  const publicSiteEditor = canUsePublicSiteEditor(data.viewer);
  const applications = useQuery(convexApi.publicMembership.listForAdmin, fullAdmin ? {} : "skip") as MembershipApplication[] | undefined;
  const adminOverview = useQuery(convexApi.admin.overview, fullAdmin ? {} : "skip") as AdminOverview | undefined;
  const publicSiteData = useQuery(convexApi.admin.publicSiteOverview, publicSiteEditor ? {} : "skip") as PublicSiteAdminData | undefined;
  const generateReport = useMutation(convexApi.admin.generateReport);
  const saveGovernanceSettings = useMutation(convexApi.admin.saveGovernanceSettings);
  const upsertPublicContentDraft = useMutation(convexApi.admin.upsertPublicContentDraft);
  const submitPublicContentChange = useMutation(convexApi.admin.submitPublicContentChange);
  const reviewPublicContentChange = useMutation(convexApi.admin.reviewPublicContentChange);
  const addPublicMediaAsset = useMutation(convexApi.admin.addPublicMediaAsset);
  const retryPublicContentPublish = useMutation(convexApi.admin.retryPublicContentPublish);
  const upsertFirm = useMutation(convexApi.admin.upsertFirm);
  const suspendFirm = useMutation(convexApi.admin.suspendFirm);
  const removeFirm = useMutation(convexApi.admin.removeFirm);
  const upsertUser = useMutation(convexApi.admin.upsertUser);
  const backfillUserMemberAccess = useMutation(convexApi.admin.backfillUserMemberAccess);
  const suspendUser = useMutation(convexApi.admin.suspendUser);
  const removeUser = useMutation(convexApi.admin.removeUser);
  const resetUserMfa = useAction(convexApi.admin.resetUserMfa);
  const sendUserInvitationEmail = useAction(convexApi.admin.sendUserInvitationEmail);
  const setFirmGovernanceAndPractices = useMutation(convexApi.admin.setFirmGovernanceAndPractices);
  const upsertPracticeArea = useMutation(convexApi.admin.upsertPracticeArea);
  const createPracticeArea = useMutation(convexApi.admin.createPracticeArea);
  const setPracticeAreaActive = useMutation(convexApi.admin.setPracticeAreaActive);
  const upsertMembershipCategoryConfig = useMutation(convexApi.admin.upsertMembershipCategoryConfig);
  const upsertAffiliationGroup = useMutation(convexApi.admin.upsertAffiliationGroup);
  const upsertTopicTag = useMutation(convexApi.admin.upsertTopicTag);
  const setTopicTagStatus = useMutation(convexApi.admin.setTopicTagStatus);
  const mergeTopicTag = useMutation(convexApi.admin.mergeTopicTag);
  const createJurisdictionTag = useMutation(convexApi.admin.createJurisdictionTag);
  const setJurisdictionTagActive = useMutation(convexApi.admin.setJurisdictionTagActive);
  const setDefaultConfidentiality = useMutation(convexApi.admin.setDefaultConfidentiality);
  const saveAdminNote = useMutation(convexApi.admin.saveAdminNote);
  const reviewMembershipApplication = useAction(convexApi.publicMembership.reviewAndEmail);
  const proposeAdmission = useMutation(convexApi.admission.proposeAdmission);
  const finalizeAdmission = useMutation(convexApi.admission.finalizeAdmission);
  const listClerkAccounts = useAction(convexApi.admin.listClerkAccounts);
  const provisionAccount = useMutation(convexApi.admin.provisionAccount);
  const generateUserPhotoUploadUrl = useMutation(convexApi.members.generateProfilePhotoUploadUrl);
  const setLegislativeSourceActive = useMutation(convexApi.legislativeNews.setSourceActive);
  const updateLegislativeSource = useMutation(convexApi.legislativeNews.updateSource);
  const deleteLegislativeSource = useMutation(convexApi.legislativeNews.deleteSource);
  const reviewLegislativeSnapshot = useMutation(convexApi.legislativeNews.reviewSnapshot);
  const refreshLegislativeJurisdiction = useAction(convexApi.legislativeNews.refreshJurisdictionNow);
  const suggestLegislativeSource = useAction(convexApi.legislativeNews.suggestSource);
  const convex = useConvex();
  const persistence: AdminPersistence = {
    generateProfilePhotoUploadUrl: () => generateUserPhotoUploadUrl({}) as Promise<string>,
    loadUserActivity: (userId) => convex.query(convexApi.admin.userActivity, { userId: userId as never }) as Promise<UserActivity>,
    generateReport: (request) => generateReport({ type: request.type, parametersJson: JSON.stringify({ from: request.from || null, to: request.to || null, format: request.format }) }) as Promise<AdminReportResult>,
    saveGovernanceSettings,
    backfillUserMemberAccess: () => backfillUserMemberAccess({}) as Promise<{ updated: number }>,
    upsertPublicContentDraft: (item) => upsertPublicContentDraft({ itemKey: item.itemKey, contentType: item.contentType, title: item.title, slug: item.slug, path: item.path, summary: item.summary, body: item.body, imageUrl: item.imageUrl ?? null, tags: item.tags ?? [], publicDate: item.publicDate ?? null, actionStatus: item.actionStatus ?? null }),
    submitPublicContentChange: (item, note) => submitPublicContentChange({ itemKey: item.itemKey, contentType: item.contentType, title: item.title, slug: item.slug, path: item.path, summary: item.summary, body: item.body, imageUrl: item.imageUrl ?? null, tags: item.tags ?? [], publicDate: item.publicDate ?? null, actionStatus: item.actionStatus ?? null, note }),
    reviewPublicContentChange: (changeId, status) => reviewPublicContentChange({ changeId: changeId as never, status }),
    addPublicMediaAsset,
    retryPublicContentPublish: (itemKey) => retryPublicContentPublish({ itemKey }),
    upsertFirm: (firm) => upsertFirm({ firmId: firm.id.startsWith("local-firm-") ? undefined : firm.id as never, name: firm.name, jurisdiction: firm.jurisdiction, address: firm.address ?? null, websiteUrl: firm.websiteUrl ?? null, telephone: firm.telephone ?? null, contactEmail: firm.contactEmail ?? null, logoUrl: firm.logoUrl ?? null, logoStorageId: (firm.logoStorageId ?? undefined) as never, avatarUrl: firm.avatarUrl ?? null, membershipCategory: firm.membershipCategory, lawyerCount: firm.lawyerCount, independenceStatement: firm.independenceStatement, status: firm.status }),
    suspendFirm: (firmId, reason) => suspendFirm({ firmId: firmId as never, reason }),
    removeFirm: (firmId, reason) => removeFirm({ firmId: firmId as never, reason }),
    upsertUser: (user) => upsertUser({ userId: user.id.startsWith("local-user-") ? undefined : user.id as never, name: user.name, firstName: user.firstName ?? null, lastName: user.lastName ?? null, email: user.email, firmId: user.firmId as never, role: user.role, memberCategory: user.memberCategory, roles: user.roles, representativeType: user.representativeType, avatarUrl: user.avatarUrl ?? null, avatarStorageId: (user.avatarStorageId ?? undefined) as never, active: user.active, notificationPreference: user.notificationPreference }),
    suspendUser: (userId, reason) => suspendUser({ userId: userId as never, reason }),
    removeUser: (userId, reason) => removeUser({ userId: userId as never, reason }),
    resetUserMfa: (userId, reason) => resetUserMfa({ userId: userId as never, reason }),
    sendUserInvitation: (userId) => sendUserInvitationEmail({ userId: userId as never }) as Promise<InvitationEmailResult>,
    setFirmGovernanceAndPractices: (input) => setFirmGovernanceAndPractices({ firmId: input.firmId as never, votingRepresentativeUserId: input.votingRepresentativeUserId as never, alternateRepresentativeUserIds: input.alternateRepresentativeUserIds as never, practiceAreaIds: input.practiceAreaIds as never }),
    upsertPracticeArea: (area) => upsertPracticeArea({ practiceAreaId: area.id.startsWith("local-practice-") ? undefined : area.id as never, name: area.name, slug: area.slug, description: area.description, active: area.active }),
    createPracticeArea,
    setPracticeAreaActive: (practiceAreaId, active) => setPracticeAreaActive({ practiceAreaId: practiceAreaId as never, active }),
    upsertMembershipCategoryConfig,
    upsertAffiliationGroup: (group) => upsertAffiliationGroup({ groupId: group.id.startsWith("local-affiliation-") ? undefined : group.id as never, name: group.name, affiliationType: group.affiliationType, explanation: group.explanation, active: group.active }),
    upsertTopicTag: (name) => upsertTopicTag({ name, status: "suggested" }),
    setTopicTagStatus: (name, status) => setTopicTagStatus({ name, status }),
    mergeTopicTag: (sourceName, targetName) => mergeTopicTag({ sourceName, targetName }),
    createJurisdictionTag: (name) => createJurisdictionTag({ name }),
    setJurisdictionTagActive: (name, active) => setJurisdictionTagActive({ name, active }),
    setDefaultConfidentiality: (value) => setDefaultConfidentiality({ value }),
    saveAdminNote,
    reviewMembershipApplication: (input) => reviewMembershipApplication({ applicationId: input.applicationId as never, status: input.status, adminNote: input.adminNote }) as Promise<InvitationEmailResult>,
    proposeAdmission: (input) => proposeAdmission({ applicationId: input.applicationId as never }) as Promise<{ proposalId: string }>,
    finalizeAdmission: (input) => finalizeAdmission({ applicationId: input.applicationId as never }) as Promise<{ firmId: string }>,
    listClerkAccounts: () => listClerkAccounts({}) as Promise<ClerkAccount[]>,
    provisionAccount: (input) => provisionAccount({ clerkUserId: input.clerkUserId, email: input.email, name: input.name, memberCategory: input.memberCategory as never }) as Promise<{ status: string; userId: string }>,
    setLegislativeSourceActive: (input) => setLegislativeSourceActive({ sourceDefinitionId: input.sourceDefinitionId as never, active: input.active }),
    updateLegislativeSource: (input) => updateLegislativeSource({ sourceDefinitionId: input.sourceDefinitionId as never, label: input.label, url: input.url }),
    deleteLegislativeSource: (input) => deleteLegislativeSource({ sourceDefinitionId: input.sourceDefinitionId as never }),
    reviewLegislativeSnapshot: (input) => reviewLegislativeSnapshot({ reviewId: input.reviewId as never, status: input.status, note: input.note }),
    refreshLegislativeJurisdiction: (jurisdiction) => refreshLegislativeJurisdiction({ jurisdiction }),
    suggestLegislativeSource: async (input) => { const result = await suggestLegislativeSource({ url: input.url, label: input.label }); return { accepted: Boolean(result?.accepted), summary: String(result?.summary ?? "") }; },
  };
  return <AdminPageContent data={data} view={view} persistence={persistence} applications={applications ?? []} adminOverview={adminOverview} publicSiteData={publicSiteData} />;
}

function AdminPageContent({ data, view, applications, adminOverview, publicSiteData, persistence }: { data: LexData; view: AdminView; applications: MembershipApplication[]; adminOverview?: AdminOverview; publicSiteData?: PublicSiteAdminData; persistence?: AdminPersistence }) {
  const loadedJurisdictionTags = data.jurisdictionTags ?? [];
  const fullAdmin = !persistence || canViewAdmin(data.viewer);
  const availableSections = fullAdmin ? sections : ["Public site"];
  const [activeSection, setActiveSection] = useState(view === "public-site" ? "Public site" : availableSections[0]);
  const [firms, setFirms] = useState(data.firms);
  const [users, setUsers] = useState(data.users);
  const [practiceAreas, setPracticeAreas] = useState(data.practiceAreas);
  const [globalTopicTags, setGlobalTopicTags] = useState<{ name: string; active: boolean; suggested: boolean }[]>(topicTags.map((name) => ({ name, active: true, suggested: false })));
  const [globalJurisdictionTags, setGlobalJurisdictionTags] = useState<{ name: string; active: boolean }[]>(
    loadedJurisdictionTags.length > 0 ? loadedJurisdictionTags.map((tag) => ({ name: tag.name, active: tag.active })) : jurisdictionTags.map((name) => ({ name, active: true })),
  );
  const [defaultConfidentiality, setDefaultConfidentiality] = useState<ConfidentialityLevel>("Network-wide");
  const [membershipConfigs, setMembershipConfigs] = useState(defaultMembershipConfigs);
  const [governanceSettings, setGovernanceSettings] = useState(defaultGovernanceSettings);
  const [affiliationGroups, setAffiliationGroups] = useState(data.affiliationGroups);
  const [membershipApplications, setMembershipApplications] = useState(applications);
  const [modal, setModal] = useState<AdminModal | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const inactiveAreas = practiceAreas.filter((area) => !area.active).length;

  useEffect(() => {
    setMembershipApplications(applications);
  }, [applications]);

  useEffect(() => {
    setFirms(data.firms);
  }, [data.firms]);

  useEffect(() => {
    setUsers(data.users);
  }, [data.users]);

  useEffect(() => {
    const nextJurisdictionTags = data.jurisdictionTags ?? [];
    if (nextJurisdictionTags.length > 0) setGlobalJurisdictionTags(nextJurisdictionTags.map((tag) => ({ name: tag.name, active: tag.active })));
  }, [data.jurisdictionTags]);

  useEffect(() => {
    setPracticeAreas(data.practiceAreas);
  }, [data.practiceAreas]);

  useEffect(() => {
    if (view === "public-site") setActiveSection("Public site");
  }, [view]);

  useEffect(() => {
    if (!availableSections.includes(activeSection)) setActiveSection(availableSections[0]);
  }, [activeSection, availableSections]);

  useEffect(() => {
    const setting = adminOverview?.adminSettings.find((item) => item.key === "governanceSettings");
    if (!setting) return;
    try {
      setGovernanceSettings({ ...defaultGovernanceSettings, ...JSON.parse(setting.valueJson) });
    } catch {
      setGovernanceSettings(defaultGovernanceSettings);
    }
  }, [adminOverview?.adminSettings]);

  if (view === "reports") {
    return <ReportsPage onExport={async (request) => {
      try {
        const result = await persistence?.generateReport?.(request);
        if (result) downloadTextFile(result.filename, result.content, result.mimeType);
        setNotice(persistence ? `${request.type} export generated.` : `${request.type} export prepared as ${request.format} in this workspace view.`);
      } catch (error) {
        setNotice(`Report was not generated: ${errorMessage(error)}`);
      }
    }} notice={notice} />;
  }

  return (
    <>
      <PageHeader title={fullAdmin ? "Admin" : "Public site editor"} subtitle={fullAdmin ? "Manage membership, public-site publishing, practice areas, governance settings and audit records." : "Prepare public pages, news and current actions for publication approval."} />
      {notice ? <div className="mb-4 rounded-xl border border-line bg-surface px-4 py-3 text-sm text-secondary">{notice}</div> : null}
      <AdminSectionNav sections={availableSections} active={view === "public-site" ? "Public site" : activeSection} onSelect={setActiveSection} />
      <div className="grid gap-5">
          {activeSection === "Mission KPIs" ? <MissionKpiPanel data={data} /> : null}
          {activeSection === "Firms and users" ? <FirmsUsersPanel firms={firms} users={users} practiceAreas={practiceAreas} firmPracticeAreas={data.firmPracticeAreas} invitations={adminOverview?.invitations ?? []} emailOutbox={adminOverview?.emailOutbox ?? []} onAddFirm={() => setModal({ kind: "firm" })} onEditFirm={(firm) => setModal({ kind: "firm", firm })} onSuspendFirm={(firm, userCount) => setModal({ kind: "suspendFirm", firm, userCount })} onRemoveFirm={(firm, userCount) => setModal({ kind: "removeFirm", firm, userCount })} onAddUser={(firmId) => setModal({ kind: "user", firmId })} onEditUser={(user) => setModal({ kind: "user", user })} onResetMfa={(user) => setModal({ kind: "resetMfa", user })} onSuspendUser={(user) => setModal({ kind: "suspendUser", user })} onRemoveUser={(user) => setModal({ kind: "removeUser", user })} onShowActivity={(user) => setModal({ kind: "userActivity", user })} onInviteUser={async (userId) => {
            try {
              const result = await persistence?.sendUserInvitation?.(userId);
              setNotice(emailNotice(result, "Clerk invitation sent.", "User invitation recorded, but the Clerk invitation was not sent."));
            } catch (error) {
              setNotice(`Invitation was not sent: ${errorMessage(error)}`);
            }
          }} onSaveFirmGovernance={async (input) => {
            try {
              await persistence?.setFirmGovernanceAndPractices?.(input);
              setNotice("Firm representatives and practice areas saved in Convex.");
            } catch (error) {
              setNotice(`Firm governance settings were not saved: ${errorMessage(error)}`);
            }
          }} /> : null}
          {activeSection === "Registered accounts" ? <RegisteredAccountsPanel persistence={persistence} /> : null}
          {activeSection === "Membership applications" ? (
            <MembershipApplicationsPanel
              applications={membershipApplications}
              onReview={async (applicationId, status, adminNote) => {
                try {
                  const result = await persistence?.reviewMembershipApplication?.({ applicationId, status, adminNote });
                  setMembershipApplications((current) => current.map((application) => application._id === applicationId ? { ...application, status, adminNote: adminNote ?? application.adminNote, updatedAt: Date.now() } : application));
                  setNotice(status === "invited" ? emailNotice(result as InvitationEmailResult | undefined, "Membership invitation saved and email sent.", "Membership invitation saved, but email was not sent.") : persistence ? "Membership application review saved in Convex." : "Membership application review updated in this workspace view.");
                } catch (error) {
                  setNotice(`Membership review was not saved: ${errorMessage(error)}`);
                }
              }}
              onProposeAdmission={persistence?.proposeAdmission ? async (applicationId) => {
                try {
                  const result = await persistence.proposeAdmission!({ applicationId });
                  setMembershipApplications((current) => current.map((application) => application._id === applicationId ? { ...application, status: "in_review", linkedProposalId: result.proposalId, updatedAt: Date.now() } : application));
                  setNotice("Admission vote opened for the network.");
                } catch (error) {
                  setNotice(`Admission vote was not opened: ${errorMessage(error)}`);
                }
              } : undefined}
              onFinalizeAdmission={persistence?.finalizeAdmission ? async (applicationId) => {
                try {
                  const result = await persistence.finalizeAdmission!({ applicationId });
                  setMembershipApplications((current) => current.map((application) => application._id === applicationId ? { ...application, status: "invited", admittedFirmId: result.firmId, updatedAt: Date.now() } : application));
                  setNotice("Admission finalised — the member firm and invitation were created.");
                } catch (error) {
                  setNotice(`Admission could not be finalised: ${errorMessage(error)}`);
                }
              } : undefined}
            />
          ) : null}
          {activeSection === "Public site" ? (
            <>
              <PublicBridgePanel data={data} onPrepare={(title) => setNotice(`Public draft prepared from private work: ${title}. Open the editor below to review and submit it.`)} />
              <PublicSiteEditorPanel viewerName={data.viewer.name} viewerRole={roleSummary(data.viewer)} canApprove={canReviewPublicSiteChanges(data.viewer)} data={publicSiteData} persistence={{
                saveDraft: (item) => persistence?.upsertPublicContentDraft?.({ ...item, contentType: item.type } as PublicContentItem) ?? Promise.resolve(),
                submitForApproval: (item, note) => persistence?.submitPublicContentChange?.({ ...item, contentType: item.type } as PublicContentItem, note) ?? Promise.resolve(),
                reviewChange: persistence?.reviewPublicContentChange,
                addMedia: persistence?.addPublicMediaAsset,
                retryPublish: persistence?.retryPublicContentPublish,
              }} onNotice={setNotice} />
            </>
          ) : null}
          {activeSection === "Legislative sources" ? <LegislativeSourcesPanel data={data} persistence={persistence} onNotice={setNotice} /> : null}
          {activeSection === "Practice areas" ? <PracticeAreasPanel data={data} practiceAreas={practiceAreas} inactiveAreas={inactiveAreas} onAdd={() => setModal({ kind: "practice", title: "Add practice area" })} onEdit={(area) => setModal({ kind: "practice", area, title: "Edit practice area" })} onCreateRecommended={async (definition) => {
            try {
              const area = practiceDefinitionToArea(definition);
              const persistedId = await (persistence?.upsertPracticeArea?.(area) ?? persistence?.createPracticeArea?.({ name: definition.name, slug: definition.slug, description: definition.description }));
              const resolvedArea = typeof persistedId === "string" ? { ...area, id: persistedId } : area;
              setPracticeAreas((current) => current.some((item) => item.slug === resolvedArea.slug) ? current : [resolvedArea, ...current]);
              setNotice(persistence ? `${definition.name} practice area saved in Convex.` : `${definition.name} practice area added in this workspace view.`);
            } catch (error) {
              setNotice(`Practice area was not added: ${errorMessage(error)}`);
            }
          }} onCreateMissingRecommended={async (definitions) => {
            try {
              const created: LexData["practiceAreas"] = [];
              for (const definition of definitions) {
                const area = practiceDefinitionToArea(definition);
                const persistedId = await (persistence?.upsertPracticeArea?.(area) ?? persistence?.createPracticeArea?.({ name: definition.name, slug: definition.slug, description: definition.description }));
                created.push(typeof persistedId === "string" ? { ...area, id: persistedId } : area);
              }
              setPracticeAreas((current) => [...created.filter((area) => !current.some((item) => item.slug === area.slug)), ...current]);
              setNotice(persistence ? `${created.length} LexMosaic practice areas saved in Convex.` : `${created.length} LexMosaic practice areas added in this workspace view.`);
            } catch (error) {
              setNotice(`Recommended practice areas were not added: ${errorMessage(error)}`);
            }
          }} onToggle={async (areaId) => {
            const area = practiceAreas.find((item) => item.id === areaId);
            const active = !area?.active;
            try {
              await persistence?.setPracticeAreaActive?.(areaId, active);
              setPracticeAreas((current) => current.map((area) => area.id === areaId ? { ...area, active, updatedAt: Date.now() } : area));
              setNotice(persistence ? "Practice area status saved in Convex." : "Practice area status updated in this workspace view.");
            } catch (error) {
              setNotice(`Practice area status was not saved: ${errorMessage(error)}`);
            }
          }} /> : null}
          {activeSection === "Post classification" ? (
            <ClassificationPanel
              topicTags={globalTopicTags}
              jurisdictionTags={globalJurisdictionTags}
              defaultConfidentiality={defaultConfidentiality}
              onAddTopic={() => setModal({ kind: "topicTag", value: "" })}
              onToggleTopic={async (name) => {
                const currentTag = globalTopicTags.find((tag) => tag.name === name);
                const active = !currentTag?.active;
                try {
                  await persistence?.setTopicTagStatus?.(name, active ? "approved" : "archived");
                  setGlobalTopicTags((current) => current.map((tag) => tag.name === name ? { ...tag, active } : tag));
                  setNotice(`${name} ${active ? "approved" : "archived"}.`);
                } catch (error) {
                  setNotice(`Topic tag was not saved: ${errorMessage(error)}`);
                }
              }}
              onMergeTopic={(name) => setModal({ kind: "topicMerge", sourceName: name })}
              onAddJurisdiction={() => setModal({ kind: "jurisdictionTag", value: "" } as AdminModal)}
              onToggleJurisdiction={async (name) => {
                const currentTag = globalJurisdictionTags.find((tag) => tag.name === name);
                const active = !currentTag?.active;
                try {
                  await persistence?.setJurisdictionTagActive?.(name, active);
                  setGlobalJurisdictionTags((current) => current.map((tag) => tag.name === name ? { ...tag, active } : tag));
                } catch (error) {
                  setNotice(`Jurisdiction tag was not saved: ${errorMessage(error)}`);
                }
              }}
              onDefaultConfidentiality={async (value) => {
                try {
                  await persistence?.setDefaultConfidentiality?.(value);
                  setDefaultConfidentiality(value);
                } catch (error) {
                  setNotice(`Confidentiality default was not saved: ${errorMessage(error)}`);
                }
              }}
            />
          ) : null}
          {activeSection === "Membership categories" ? <MembershipPanel configs={membershipConfigs} firms={firms} onConfigure={(config) => setModal({ kind: "membership", config })} /> : null}
          {activeSection === "Affiliation groups" ? <AffiliationPanel groups={affiliationGroups} firms={firms} onCreate={() => setModal({ kind: "affiliation", title: "Create affiliation group" })} onEdit={(group) => setModal({ kind: "affiliation", group, title: "Edit affiliation group" })} /> : null}
          {activeSection === "Governance settings" ? <GovernancePanel settings={governanceSettings} onOpenNote={() => setModal({ kind: "health", title: "Governance settings" })} onSave={async (settings) => {
            try {
              await persistence?.saveGovernanceSettings?.(settings);
              setGovernanceSettings(settings);
              setNotice(persistence ? "Governance settings saved in Convex." : "Governance settings updated in this workspace view.");
            } catch (error) {
              setNotice(`Governance settings were not saved: ${errorMessage(error)}`);
            }
          }} /> : null}
          {activeSection === "Reports and exports" ? <ReportsShortcut /> : null}
          {activeSection === "Audit log" ? <AuditPanel data={data} auditLogs={adminOverview?.auditLogs} onOpen={(record) => setModal({ kind: "audit", record })} /> : null}
          {activeSection === "System health" ? <SystemHealth data={data} users={users} firms={firms} adminOverview={adminOverview} publicSiteData={publicSiteData} onBackfill={async () => {
            try {
              const result = await persistence?.backfillUserMemberAccess?.();
              setNotice(result ? `Member categories and roles backfilled for ${result.updated} users.` : "Member categories and roles backfilled in this workspace view.");
            } catch (error) {
              setNotice(`Member access backfill was not completed: ${errorMessage(error)}`);
            }
          }} onRefresh={() => {
            setModal({ kind: "health", title: "System health refreshed" });
            setNotice("System health refreshed.");
          }} /> : null}
      </div>

      {modal?.kind === "firm" ? (
        <FirmEditor
          firm={modal.firm}
          practiceAreas={practiceAreas}
          selectedPracticeAreaIds={data.firmPracticeAreas.filter((link) => link.firmId === modal.firm?.id).map((link) => link.practiceAreaId)}
          onUploadLogo={persistence?.generateProfilePhotoUploadUrl ? async (file) => {
            const uploadUrl = await persistence.generateProfilePhotoUploadUrl!();
            const res = await fetch(uploadUrl, { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
            if (!res.ok) throw new Error("Logo upload failed.");
            const { storageId } = (await res.json()) as { storageId: string };
            return storageId;
          } : undefined}
          onClose={() => setModal(null)}
          onSave={async (updated, practiceAreaIds) => {
            try {
              const persistedId = await (persistence?.upsertFirm ?? persistence?.updateFirm)?.(updated);
              const resolvedFirm = typeof persistedId === "string" && updated.id.startsWith("local-firm-") ? { ...updated, id: persistedId } : updated;
              await persistence?.setFirmGovernanceAndPractices?.({
                firmId: resolvedFirm.id,
                votingRepresentativeUserId: resolvedFirm.votingRepresentativeUserId ?? null,
                alternateRepresentativeUserIds: resolvedFirm.alternateRepresentativeUserIds ?? [],
                practiceAreaIds,
              });
              setFirms((current) => (current.some((firm) => firm.id === resolvedFirm.id) ? current.map((firm) => firm.id === resolvedFirm.id ? resolvedFirm : firm) : [resolvedFirm, ...current]));
              setModal(null);
              setNotice(persistence ? `${resolvedFirm.name} and practice areas saved in Convex.` : `${resolvedFirm.name} saved.`);
            } catch (error) {
              setNotice(`Firm was not saved: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "user" ? (
        <UserEditor
          user={modal.user}
          defaultFirmId={modal.firmId}
          firms={firms}
          onUploadPhoto={persistence?.generateProfilePhotoUploadUrl ? async (file) => {
            const uploadUrl = await persistence.generateProfilePhotoUploadUrl!();
            const res = await fetch(uploadUrl, { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
            if (!res.ok) throw new Error("Photo upload failed.");
            const { storageId } = (await res.json()) as { storageId: string };
            return storageId;
          } : undefined}
          onClose={() => setModal(null)}
          onSave={async (savedUser) => {
            try {
              const persistedId = await persistence?.upsertUser?.(savedUser);
              const resolvedUser = typeof persistedId === "string" && savedUser.id.startsWith("local-user-") ? { ...savedUser, id: persistedId } : savedUser;
              setUsers((current) => (current.some((user) => user.id === resolvedUser.id) ? current.map((user) => user.id === resolvedUser.id ? resolvedUser : user) : [resolvedUser, ...current]));
              setModal(null);
              setNotice(persistence ? `${resolvedUser.name} saved in Convex. The user can sign in when their authentication account uses ${resolvedUser.email}.` : `${resolvedUser.name} saved.`);
            } catch (error) {
              setNotice(`User was not saved: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "resetMfa" ? (
        <AdminDangerDialog
          title="Reset MFA"
          description={`${modal.user.name}'s enrolled MFA methods will be removed in Clerk. The user can sign in and set up MFA again from their account settings.`}
          confirmLabel="Reset MFA"
          defaultReason="User locked out of MFA"
          requireTypedValue={modal.user.email}
          warning="This removes the user's current MFA enrollments. Verify the requester's identity before continuing and ask the user to set up MFA again after sign-in."
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            try {
              await persistence?.resetUserMfa?.(modal.user.id, reason);
              setModal(null);
              setNotice(`${modal.user.name}'s MFA enrollments were reset in Clerk and the action was written to the audit log.`);
            } catch (error) {
              setNotice(`MFA was not reset: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "suspendUser" ? (
        <AdminDangerDialog
          title="Suspend user"
          description={`${modal.user.name} will no longer be able to access the private LexMosaic workspace.`}
          confirmLabel="Suspend user"
          defaultReason="Suspended by administrator"
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            try {
              await persistence?.suspendUser?.(modal.user.id, reason);
              setUsers((current) => current.map((user) => user.id === modal.user.id ? { ...user, active: false, updatedAt: Date.now() } : user));
              setModal(null);
              setNotice(`${modal.user.name} has been suspended.`);
            } catch (error) {
              setNotice(`User was not suspended: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "removeUser" ? (
        <AdminDangerDialog
          title="Remove user permanently"
          description={`${modal.user.name} will be permanently removed from LexMosaic membership records. This cannot be undone.`}
          confirmLabel="Remove user"
          defaultReason="Removed by administrator"
          requireTypedValue={modal.user.email}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            try {
              await persistence?.removeUser?.(modal.user.id, reason);
              setUsers((current) => current.filter((user) => user.id !== modal.user.id));
              setModal(null);
              setNotice(`${modal.user.name} has been removed permanently.`);
            } catch (error) {
              setNotice(`User was not removed: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "suspendFirm" ? (
        <AdminDangerDialog
          title="Suspend firm"
          description={`${modal.firm.name} will be marked suspended. ${modal.userCount} linked user${modal.userCount === 1 ? "" : "s"} will be deactivated and cannot access the private workspace.`}
          confirmLabel="Suspend firm"
          defaultReason="Firm suspended by administrator"
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            try {
              await persistence?.suspendFirm?.(modal.firm.id, reason);
              setFirms((current) => current.map((firm) => firm.id === modal.firm.id ? { ...firm, status: "suspended", suspendedAt: Date.now(), updatedAt: Date.now() } : firm));
              setUsers((current) => current.map((user) => user.firmId === modal.firm.id ? { ...user, active: false, updatedAt: Date.now() } : user));
              setModal(null);
              setNotice(`${modal.firm.name} has been suspended and linked users were deactivated.`);
            } catch (error) {
              setNotice(`Firm was not suspended: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "removeFirm" ? (
        <AdminDangerDialog
          title="Remove firm permanently"
          description={`${modal.firm.name} and ${modal.userCount} linked user${modal.userCount === 1 ? "" : "s"} will be permanently removed from membership records. This cannot be undone.`}
          confirmLabel="Remove firm"
          defaultReason="Firm removed by administrator"
          requireTypedValue={modal.firm.name}
          onClose={() => setModal(null)}
          onConfirm={async (reason) => {
            try {
              await persistence?.removeFirm?.(modal.firm.id, reason);
              setFirms((current) => current.filter((firm) => firm.id !== modal.firm.id));
              setUsers((current) => current.filter((user) => user.firmId !== modal.firm.id));
              setModal(null);
              setNotice(`${modal.firm.name} has been removed permanently.`);
            } catch (error) {
              setNotice(`Firm was not removed: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "membership" ? (
        <MembershipCategoryEditor
          config={modal.config}
          onClose={() => setModal(null)}
          onSave={async (updated) => {
            try {
              await persistence?.upsertMembershipCategoryConfig?.(updated);
              setMembershipConfigs((current) => current.map((item) => item.category === updated.category ? updated : item));
              setModal(null);
              setNotice(persistence ? `${updated.category} category saved in Convex.` : `${updated.category} category updated in this workspace view.`);
            } catch (error) {
              setNotice(`Membership category was not saved: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "affiliation" ? (
        <AffiliationGroupEditor
          group={modal.group}
          onClose={() => setModal(null)}
          onSave={async (savedGroup) => {
            try {
              await persistence?.upsertAffiliationGroup?.(savedGroup);
              setAffiliationGroups((current) => current.some((item) => item.id === savedGroup.id) ? current.map((item) => item.id === savedGroup.id ? savedGroup : item) : [savedGroup, ...current]);
              setModal(null);
              setNotice(persistence ? `${savedGroup.name} affiliation group saved in Convex.` : `${savedGroup.name} affiliation group saved in this workspace view.`);
            } catch (error) {
              setNotice(`Affiliation group was not saved: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "topicTag" ? (
        <TopicTagEditor
          onClose={() => setModal(null)}
          onSave={async (name) => {
            try {
              if (name.trim()) await persistence?.upsertTopicTag?.(name.trim());
              if (name.trim()) setGlobalTopicTags((current) => [{ name: name.trim() as TopicTag, active: false, suggested: true }, ...current]);
              setModal(null);
              setNotice(persistence ? `${name} added as a suggested topic tag in Convex.` : `${name} added as a suggested topic tag pending admin approval.`);
            } catch (error) {
              setNotice(`Topic tag was not saved: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "jurisdictionTag" ? (
        <JurisdictionTagEditor
          onClose={() => setModal(null)}
          onSave={async (name) => {
            const trimmed = name.trim();
            if (!trimmed) return;
            try {
              await persistence?.createJurisdictionTag?.(trimmed);
              setGlobalJurisdictionTags((current) => [{ name: trimmed, active: true }, ...current]);
              setNotice(persistence ? `${trimmed} jurisdiction tag saved in Convex.` : `${trimmed} jurisdiction tag added.`);
              setModal(null);
            } catch (error) {
              setNotice(`Jurisdiction tag was not saved: ${errorMessage(error)}`);
            }
          }}
        />
      ) : null}

      {modal?.kind === "topicMerge" ? (
        <TopicMergeEditor
          sourceName={modal.sourceName}
          topicTags={globalTopicTags.map((tag) => tag.name)}
          onClose={() => setModal(null)}
          onSave={async (targetName) => {
            if (targetName && targetName !== modal.sourceName) {
              try {
                await persistence?.mergeTopicTag?.(modal.sourceName, targetName);
                setGlobalTopicTags((current) => current.filter((tag) => tag.name !== modal.sourceName));
                setNotice(persistence ? `${modal.sourceName} merged into ${targetName} in Convex.` : `${modal.sourceName} merged into ${targetName} in this workspace view.`);
              } catch (error) {
                setNotice(`Topic tag merge was not saved: ${errorMessage(error)}`);
                return;
              }
            }
            setModal(null);
          }}
        />
      ) : null}

      {modal?.kind === "practice" ? (
        <PracticeAreaEditor
          area={modal.area}
          onClose={() => setModal(null)}
          onSave={async (area) => {
            const persistedId = await (persistence?.upsertPracticeArea?.(area) ?? persistence?.createPracticeArea?.({ name: area.name, slug: area.slug, description: area.description }));
            const resolvedArea = typeof persistedId === "string" && area.id.startsWith("local-practice-") ? { ...area, id: persistedId } : area;
            setPracticeAreas((current) => current.some((item) => item.id === resolvedArea.id) ? current.map((item) => item.id === resolvedArea.id ? resolvedArea : item) : [resolvedArea, ...current]);
            setModal(null);
            setNotice(persistence ? `${resolvedArea.name} practice area saved in Convex.` : `${resolvedArea.name} practice area saved in this workspace view.`);
          }}
        />
      ) : null}

      {modal?.kind === "audit" ? (
        <AuditDetailModal record={modal.record} onClose={() => setModal(null)} />
      ) : null}

      {modal?.kind === "userActivity" ? (
        <UserActivityModal user={modal.user} load={persistence?.loadUserActivity} onClose={() => setModal(null)} />
      ) : null}

      {modal && modal.kind !== "firm" && modal.kind !== "user" && modal.kind !== "resetMfa" && modal.kind !== "suspendUser" && modal.kind !== "removeUser" && modal.kind !== "suspendFirm" && modal.kind !== "removeFirm" && modal.kind !== "membership" && modal.kind !== "affiliation" && modal.kind !== "topicTag" && modal.kind !== "jurisdictionTag" && modal.kind !== "topicMerge" && modal.kind !== "practice" && modal.kind !== "audit" && modal.kind !== "userActivity" ? (
        <SectionActionModal modal={modal} onClose={() => setModal(null)} onConfirm={async (input) => {
          try {
            await persistence?.saveAdminNote?.({ kind: modal.kind, ...input });
            setNotice(persistence ? `${input.title} saved in Convex.` : `${input.title} saved in this workspace view.`);
            setModal(null);
          } catch (error) {
            setNotice(`Admin note was not saved: ${errorMessage(error)}`);
          }
        }} />
      ) : null}
    </>
  );
}

function ReportsPage({ onExport, notice }: { onExport: (request: ReportRequest) => Promise<void>; notice: string | null }) {
  const [type, setType] = useState(reportTypes[0]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [format, setFormat] = useState("CSV");
  const [exporting, setExporting] = useState(false);
  const runExport = async () => {
    setExporting(true);
    try {
      await onExport({ type, from, to, format });
    } finally {
      setExporting(false);
    }
  };
  return (
    <>
      <PageHeader title="Reports" subtitle="Build one focused export at a time." />
      {notice ? <div className="mb-4 rounded-xl border border-line bg-surface px-4 py-3 text-sm text-secondary">{notice}</div> : null}
      <Card>
        <div className="grid gap-4 md:grid-cols-4">
          <Select label="Report type" values={reportTypes} value={type} onChange={setType} />
          <label>
            <span className="mb-1 block text-xs font-medium text-muted">From</span>
            <input type="date" className="min-h-11 w-full rounded-md border border-line bg-elevated px-3" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label>
            <span className="mb-1 block text-xs font-medium text-muted">To</span>
            <input type="date" className="min-h-11 w-full rounded-md border border-line bg-elevated px-3" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
          <Select label="Format" values={["CSV", "PDF"]} value={format} onChange={setFormat} />
        </div>
        <div className="mt-4">
          <Button type="button" onClick={runExport} disabled={exporting}>
            <span className="inline-flex items-center gap-2">
              <Download className="h-4 w-4" />
              {exporting ? "Exporting..." : "Export"}
            </span>
          </Button>
        </div>
      </Card>
    </>
  );
}

function SectionIcon({ name }: { name: string }) {
  const className = "h-4 w-4";
  if (name.includes("Mission")) return <Activity className={className} />;
  if (name.includes("Firms")) return <UsersRound className={className} />;
  if (name.includes("Applications")) return <FileText className={className} />;
  if (name.includes("Public")) return <Globe2 className={className} />;
  if (name.includes("Practice")) return <ListChecks className={className} />;
  if (name.includes("Reports")) return <Download className={className} />;
  if (name.includes("Audit")) return <Activity className={className} />;
  if (name.includes("System")) return <Settings className={className} />;
  return <ShieldCheck className={className} />;
}

const PRIMARY_ADMIN_SECTIONS = ["Mission KPIs", "Firms and users", "Registered accounts", "Membership applications", "Public site"];

function AdminSectionNav({ sections, active, onSelect }: { sections: string[]; active: string; onSelect: (section: string) => void }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const primary = sections.filter((section) => PRIMARY_ADMIN_SECTIONS.includes(section));
  const overflow = sections.filter((section) => !PRIMARY_ADMIN_SECTIONS.includes(section));
  const overflowActive = overflow.includes(active);
  const tabClass = (isActive: boolean) =>
    `-mb-px inline-flex items-center gap-2 whitespace-nowrap rounded-t-md border-b-2 px-3.5 py-2.5 text-[13.5px] transition ${
      isActive ? "border-primary font-semibold text-primary" : "border-transparent font-medium text-secondary hover:bg-surfaceAlt hover:text-ink"
    }`;
  return (
    <div className="relative mb-5 flex flex-wrap items-center gap-1 border-b border-line">
      {primary.map((section) => (
        <button key={section} type="button" className={tabClass(section === active)} onClick={() => onSelect(section)}>
          <SectionIcon name={section} />
          {section}
        </button>
      ))}
      {overflow.length > 0 ? (
        <div className="relative">
          <button type="button" className={tabClass(overflowActive)} onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen}>
            {overflowActive ? <SectionIcon name={active} /> : null}
            {overflowActive ? active : "More"}
            <ChevronDown className={`h-4 w-4 transition ${moreOpen ? "rotate-180" : ""}`} />
          </button>
          {moreOpen ? (
            <div className="absolute left-0 top-[calc(100%+4px)] z-50 min-w-[210px] rounded-lg border border-line bg-surface p-1.5 shadow-soft">
              {overflow.map((section) => (
                <button
                  key={section}
                  type="button"
                  onClick={() => { onSelect(section); setMoreOpen(false); }}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium ${section === active ? "bg-cobaltSoft text-cobaltDeep" : "text-ink hover:bg-divider"}`}
                >
                  <SectionIcon name={section} />
                  {section}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {moreOpen ? <button type="button" aria-hidden tabIndex={-1} className="fixed inset-0 z-40 cursor-default" onClick={() => setMoreOpen(false)} /> : null}
    </div>
  );
}

function MissionKpiPanel({ data }: { data: LexData }) {
  return <AdminMissionDashboard data={data} />;
}

function PublicBridgePanel({ data, onPrepare }: { data: LexData; onPrepare: (title: string) => void }) {
  const rows = [
    ...data.lobbyingProjects
      .filter((project) => project.mandateStatus === "approved" || project.transparencyStatus === "recorded")
      .map((project) => ({ id: project.id, type: "Legislative action", title: project.title, summary: project.objective })),
    ...data.proposals
      .filter((proposal) => proposal.status === "decided" || proposal.votingClosesAt < Date.now())
      .slice(0, 3)
      .map((proposal) => {
        const discussion = data.discussions.find((item) => item.id === proposal.discussionId);
        return { id: proposal.id, type: "Decision", title: discussion?.title ?? proposal.proposalText, summary: proposal.consolidatedText ?? proposal.proposalText };
      }),
  ];
  return (
    <Section title="Public bridge" kicker="Private work suitable for public drafting">
      {rows.length > 0 ? (
        <div className="grid gap-2">
          {rows.map((row) => (
            <Card key={`${row.type}-${row.id}`} className="rounded-[10px] p-3">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <Globe2 className="h-4 w-4 text-primary" />
                <div className="min-w-0 flex-1">
                  <Label>{row.type}</Label>
                  <div className="mt-1 font-semibold text-ink">{row.title}</div>
                  <p className="mt-1 line-clamp-2 text-sm text-secondary">{row.summary}</p>
                </div>
                <Button type="button" variant="secondary" onClick={() => onPrepare(row.title)}>Prepare public draft</Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <p className="text-sm text-secondary">Approved decisions and legislative actions will appear here before they are drafted for the public site.</p>
        </Card>
      )}
    </Section>
  );
}

function RegisteredAccountsPanel({ persistence }: { persistence?: AdminPersistence }) {
  const [accounts, setAccounts] = useState<ClerkAccount[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const load = async () => {
    if (!persistence?.listClerkAccounts) return;
    setLoading(true);
    setError(null);
    try {
      setAccounts(await persistence.listClerkAccounts());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load registered accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provision = async (account: ClerkAccount, memberCategory: string) => {
    if (!persistence?.provisionAccount) return;
    setBusyEmail(account.email);
    setError(null);
    try {
      await persistence.provisionAccount({ clerkUserId: account.clerkId, email: account.email, name: account.name, memberCategory });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not add this account.");
    } finally {
      setBusyEmail(null);
    }
  };

  const provisionedCount = accounts?.filter((a) => a.provisioned).length ?? 0;

  return (
    <Section
      title="Registered accounts"
      kicker="Everyone signed up via Clerk"
      aside={<Button type="button" variant="secondary" onClick={() => void load()} disabled={loading || !persistence}>{loading ? "Refreshing…" : "Refresh"}</Button>}
    >
      {!persistence ? (
        <Card><p className="text-sm text-secondary">Available in the live workspace.</p></Card>
      ) : error ? (
        <Card className="border-rose-200 bg-rose-50/40"><p className="text-sm text-rose-800">{error}</p></Card>
      ) : accounts === null ? (
        <Card><p className="text-sm text-secondary">{loading ? "Loading registered accounts from Clerk…" : "—"}</p></Card>
      ) : accounts.length === 0 ? (
        <Card><p className="text-sm text-secondary">No registered accounts found in Clerk.</p></Card>
      ) : (
        <>
          <p className="mb-3 text-sm text-secondary">{accounts.length} registered · {provisionedCount} in the app · {accounts.length - provisionedCount} not yet provisioned.</p>
          <div className="grid gap-2">
            {accounts.map((account) => (
              <Card key={account.clerkId}>
                <div className="flex flex-wrap items-center gap-3">
                  <UserRound className="h-4 w-4 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink">{account.name}</div>
                    <div className="truncate text-xs text-secondary">
                      {account.email} · registered {new Date(account.createdAt).toLocaleDateString()}{account.lastSignInAt ? ` · last sign-in ${new Date(account.lastSignInAt).toLocaleDateString()}` : " · never signed in"}
                    </div>
                  </div>
                  {account.provisioned ? (
                    <>
                      <Tag>{(account.memberCategory ?? "member").replace(/_/g, " ")}</Tag>
                      <Tag>{account.hasFirm ? "has firm" : "no firm"}</Tag>
                      <Tag>{account.active ? "active" : "inactive"}</Tag>
                      <Tag>in app</Tag>
                    </>
                  ) : (
                    <>
                      <Tag>not in app</Tag>
                      <Button type="button" variant="secondary" disabled={busyEmail === account.email} onClick={() => void provision(account, "observer")}>{busyEmail === account.email ? "Adding…" : "Add as observer"}</Button>
                      <Button type="button" variant="secondary" disabled={busyEmail === account.email} onClick={() => void provision(account, "full_member_representative")}>Add as member</Button>
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
      <p className="mt-2 text-[11px] text-muted">Provisioned accounts appear under “Firms and users”, where you can fine-tune member category, technical rights, representative type and firm.</p>
    </Section>
  );
}

function MembershipApplicationsPanel({ applications, onReview, onProposeAdmission, onFinalizeAdmission }: { applications: MembershipApplication[]; onReview: (applicationId: string, status: MembershipApplicationStatus, adminNote?: string) => void; onProposeAdmission?: (applicationId: string) => void; onFinalizeAdmission?: (applicationId: string) => void }) {
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [draftStatuses, setDraftStatuses] = useState<Record<string, MembershipApplicationStatus>>({});
  const statuses: MembershipApplicationStatus[] = ["new", "in_review", "follow_up", "invited", "declined", "archived"];
  return (
    <Section title="Membership applications" kicker="Public intake">
      <div className="grid gap-3">
        {applications.length > 0 ? applications.map((application) => {
          const note = draftNotes[application._id] ?? application.adminNote ?? "";
          const status = draftStatuses[application._id] ?? application.status;
          return (
            <Card key={application._id}>
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold">{application.firmName}</h3>
                    <Tag>{application.status.replace(/_/g, " ")}</Tag>
                    <Tag>{application.membershipInterest}</Tag>
                  </div>
                  <div className="mt-1 text-sm text-secondary">{application.jurisdiction} · {application.lawyerCount} lawyers · submitted {new Date(application.createdAt).toLocaleDateString()}</div>
                  <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    <InfoLine label="Contact" value={`${application.contactName}, ${application.roleTitle}`} />
                    <InfoLine label="Email" value={application.contactEmail} />
                    <InfoLine label="Independence" value={application.independenceStatement} />
                    <InfoLine label="Practice focus" value={application.practiceFocus} />
                  </div>
                  {application.message ? <p className="mt-3 text-sm leading-6 text-secondary">{application.message}</p> : null}
                  {application.websiteUrl ? <a className="mt-3 inline-flex text-sm font-semibold text-primary" href={application.websiteUrl} target="_blank" rel="noreferrer">Open website</a> : null}
                </div>
                <div className="grid gap-3">
                  <label>
                    <span className="mb-1 block text-xs font-medium text-muted">Review note</span>
                    <textarea className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2" value={note} onChange={(event) => setDraftNotes((current) => ({ ...current, [application._id]: event.target.value }))} />
                  </label>
                  <Select label="Review status" values={statuses.map((item) => item.replace(/_/g, " "))} value={status.replace(/_/g, " ")} onChange={(value) => setDraftStatuses((current) => ({ ...current, [application._id]: value.replace(/ /g, "_") as MembershipApplicationStatus }))} />
                  <Button type="button" onClick={() => onReview(application._id, status, note)}>Save review</Button>
                  {onProposeAdmission && !application.linkedProposalId && !application.admittedFirmId && application.status !== "invited" && application.status !== "declined" ? (
                    <Button type="button" variant="secondary" onClick={() => onProposeAdmission(application._id)}>Open admission vote</Button>
                  ) : null}
                  {application.linkedProposalId && !application.admittedFirmId ? (
                    <>
                      <div className="rounded-lg border border-divider bg-canvas px-3 py-2 text-xs text-secondary">Admission vote open. Finalise once the vote is certified adopted.</div>
                      {onFinalizeAdmission ? <Button type="button" variant="secondary" onClick={() => onFinalizeAdmission(application._id)}>Finalise admission</Button> : null}
                    </>
                  ) : null}
                  {application.admittedFirmId ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">Admitted — member firm created.</div> : null}
                </div>
              </div>
            </Card>
          );
        }) : (
          <Card>
            <p className="text-sm text-secondary">No membership applications have been submitted yet.</p>
          </Card>
        )}
      </div>
    </Section>
  );
}

function FirmsUsersPanel({
  firms,
  users,
  practiceAreas,
  firmPracticeAreas,
  invitations,
  emailOutbox,
  onAddFirm,
  onEditFirm,
  onSuspendFirm,
  onRemoveFirm,
  onAddUser,
  onEditUser,
  onResetMfa,
  onSuspendUser,
  onRemoveUser,
  onShowActivity,
  onInviteUser,
  onSaveFirmGovernance,
}: {
  firms: Firm[];
  users: User[];
  practiceAreas: LexData["practiceAreas"];
  firmPracticeAreas: LexData["firmPracticeAreas"];
  invitations: AdminInvitationRecord[];
  emailOutbox: AdminEmailOutboxRecord[];
  onAddFirm: () => void;
  onEditFirm: (firm: Firm) => void;
  onSuspendFirm: (firm: Firm, userCount: number) => void;
  onRemoveFirm: (firm: Firm, userCount: number) => void;
  onAddUser: (firmId?: string) => void;
  onEditUser: (user: User) => void;
  onResetMfa: (user: User) => void;
  onSuspendUser: (user: User) => void;
  onRemoveUser: (user: User) => void;
  onShowActivity: (user: User) => void;
  onInviteUser: (userId: string) => Promise<void>;
  onSaveFirmGovernance: (input: { firmId: string; votingRepresentativeUserId?: string | null; alternateRepresentativeUserIds: string[]; practiceAreaIds: string[] }) => Promise<void>;
}) {
  const unassignedUsers = users.filter((user) => !user.firmId || !firms.some((firm) => firm.id === user.firmId));
  const [expandedFirmId, setExpandedFirmId] = useState<string | null>(null);
  const [menuFirmId, setMenuFirmId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [segment, setSegment] = useState<UserStatusKind | "all">("all");
  const [tier, setTier] = useState<string>("all");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const invitationByUserId = new Map(invitations.map((invite) => [invite.userId, invite]));
  const emailByUserId = latestEmailByUserId(emailOutbox);

  const q = query.trim().toLowerCase();
  const firmMatches = (firm: Firm) => !q || [firm.name, firm.jurisdiction, firm.websiteUrl, firm.contactEmail].some((value) => (value ?? "").toLowerCase().includes(q));
  const userMatches = (user: User) => !q || [user.name, user.email, shortRole(user), roleSummary(user)].some((value) => (value ?? "").toLowerCase().includes(q));
  const userInSegment = (user: User) => segment === "all" || statusKind(user) === segment;

  const visibleFirms = firms
    .filter((firm) => tier === "all" || firm.membershipCategory === tier)
    .map((firm) => {
      const firmUsers = users.filter((user) => user.firmId === firm.id);
      const fMatch = firmMatches(firm);
      const visibleUsers = firmUsers.filter((user) => (fMatch || userMatches(user)) && userInSegment(user));
      let show: boolean;
      if (segment === "all") show = fMatch || visibleUsers.length > 0;
      else if (segment === "suspended") show = (firm.status === "suspended" && fMatch) || visibleUsers.length > 0;
      else show = visibleUsers.length > 0;
      return { firm, firmUsers, visibleUsers, show };
    })
    .filter((entry) => entry.show);

  const unassignedVisible = tier === "all" ? unassignedUsers.filter((user) => userMatches(user) && userInSegment(user)) : [];

  const toggleUser = (id: string) => setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const setFirmSelection = (ids: string[], on: boolean) => setSelected((current) => { const next = new Set(current); for (const id of ids) { if (on) next.add(id); else next.delete(id); } return next; });
  const clearSelection = () => setSelected(new Set());
  const handlers: UserRowHandlers = { onShowActivity, onInviteUser, onResetMfa, onEditUser, onSuspendUser, onRemoveUser };
  const segments: { key: UserStatusKind | "all"; label: string }[] = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "invited", label: "Invited" },
    { key: "suspended", label: "Suspended" },
  ];

  return (
    <Section title="Firms and users" kicker="Membership">
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="flex w-[300px] max-w-full items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 focus-within:border-primary">
          <Search className="h-4 w-4 shrink-0 text-muted" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter firms or members…" className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-muted" />
          {query ? <button type="button" aria-label="Clear filter" onClick={() => setQuery("")} className="grid shrink-0 place-items-center text-muted hover:text-ink"><X className="h-4 w-4" /></button> : null}
        </div>
        <div className="flex gap-0.5 rounded-lg bg-divider p-[3px]">
          {segments.map((option) => (
            <button key={option.key} type="button" onClick={() => setSegment(option.key)} className={`rounded-md px-3 py-1.5 text-[12.5px] transition ${segment === option.key ? "bg-surface font-semibold text-ink shadow-sm" : "font-medium text-muted hover:text-ink"}`}>{option.label}</button>
          ))}
        </div>
        <select value={tier} onChange={(event) => setTier(event.target.value)} className="rounded-lg border border-line bg-surface px-3 py-2 text-[13px] font-medium text-ink">
          <option value="all">All tiers</option>
          {membershipCategories.map((category) => <option key={category} value={category}>{category}</option>)}
        </select>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="secondary" onClick={onAddFirm}><Building2 className="h-4 w-4" /> Add firm</Button>
          <Button type="button" onClick={() => onAddUser(firms[0]?.id)}><Plus className="h-4 w-4" /> Add user</Button>
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white shadow-soft">
          <span className="mr-1 text-[13px] font-semibold">{selected.size} selected</span>
          <button type="button" onClick={() => { void Promise.all([...selected].map((id) => onInviteUser(id))).finally(clearSelection); }} className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-white/10"><Send className="h-4 w-4" /> Resend invite</button>
          <button type="button" onClick={clearSelection} className="ml-1 rounded-md px-2 py-1 text-[13px] font-medium text-white/80 hover:text-white">Clear</button>
        </div>
      ) : null}

      <div className="grid gap-4">
        {visibleFirms.map(({ firm, firmUsers, visibleUsers }) => {
          const host = firm.websiteUrl ? firm.websiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "") : null;
          const allSelected = firmUsers.length > 0 && firmUsers.every((user) => selected.has(user.id));
          return (
            <div key={firm.id} className="overflow-hidden rounded-xl border border-line bg-surface shadow-soft">
              <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surfaceAlt px-4 py-3.5">
                <SelectBox checked={allSelected} onChange={() => setFirmSelection(firmUsers.map((user) => user.id), !allSelected)} />
                <FirmLogo firm={firm} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[16px] font-semibold text-ink">{firm.name}</span>
                    <TierBadge category={firm.membershipCategory} />
                    {firm.status === "suspended" ? <StatusPill kind="suspended" /> : null}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-muted">
                    <span>{firm.jurisdiction}</span>
                    <span className="text-line">·</span>
                    <span>{firmUsers.length} user{firmUsers.length === 1 ? "" : "s"}</span>
                    {host ? <><span className="text-line">·</span><a href={firm.websiteUrl ?? undefined} target="_blank" rel="noreferrer" className="font-medium text-navy hover:underline">{host}</a></> : null}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button type="button" title="Add user" aria-label="Add user" onClick={() => onAddUser(firm.id)} className="grid h-[30px] w-[30px] place-items-center rounded-md bg-primary text-white shadow-[0_2px_8px_rgba(122,28,44,0.22)] transition hover:brightness-95"><UserPlus className="h-4 w-4" /></button>
                  <IconBtn icon={Pencil} title="Edit firm" onClick={() => onEditFirm(firm)} />
                  <IconBtn icon={UsersRound} title="Representatives" onClick={() => setExpandedFirmId((current) => current === firm.id ? null : firm.id)} />
                  <div className="relative">
                    <button type="button" aria-label="More firm actions" onClick={() => setMenuFirmId((current) => current === firm.id ? null : firm.id)} className={`grid h-[34px] w-[34px] place-items-center rounded-lg border border-line text-muted transition hover:bg-hover hover:text-ink ${menuFirmId === firm.id ? "bg-hover text-ink" : "bg-surface"}`}><MoreHorizontal className="h-4 w-4" /></button>
                    {menuFirmId === firm.id ? (
                      <div className="absolute right-0 top-[calc(100%+5px)] z-50 min-w-[190px] rounded-lg border border-line bg-surface p-1.5 shadow-soft">
                        <FirmMenuLink to={`/members/${firm.id}`} icon={LayoutGrid} label="View public profile" onClick={() => setMenuFirmId(null)} />
                        <FirmMenuButton icon={Mail} label="Contact details" onClick={() => { setMenuFirmId(null); onEditFirm(firm); }} />
                        <div className="my-1.5 h-px bg-divider" />
                        {firm.status !== "suspended" ? <FirmMenuButton icon={Pause} label="Suspend firm" tone="warn" onClick={() => { setMenuFirmId(null); onSuspendFirm(firm, firmUsers.length); }} /> : null}
                        <FirmMenuButton icon={Trash2} label="Remove firm" tone="danger" onClick={() => { setMenuFirmId(null); onRemoveFirm(firm, firmUsers.length); }} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              {visibleUsers.length > 0 ? (
                <UserTable users={visibleUsers} selected={selected} onToggleUser={toggleUser} invitationByUserId={invitationByUserId} emailByUserId={emailByUserId} handlers={handlers} />
              ) : (
                <div className="px-4 py-6 text-center text-[13px] text-muted">No members match the current filter.</div>
              )}
              {expandedFirmId === firm.id ? (
                <div className="border-t border-line p-4">
                  <FirmGovernanceControls
                    firm={firm}
                    users={firmUsers}
                    practiceAreas={practiceAreas}
                    selectedPracticeAreaIds={firmPracticeAreas.filter((link) => link.firmId === firm.id).map((link) => link.practiceAreaId)}
                    onSave={onSaveFirmGovernance}
                  />
                </div>
              ) : null}
            </div>
          );
        })}

        {unassignedVisible.length > 0 ? (
          <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-soft">
            <div className="flex items-center gap-3 border-b border-line bg-surfaceAlt px-4 py-3.5">
              <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-lg bg-divider text-muted"><UserRound className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold text-ink">Users without an active firm</div>
                <p className="text-[12.5px] text-muted">Linked to no visible active firm — edit a user to assign a firm.</p>
              </div>
            </div>
            <UserTable users={unassignedVisible} selected={selected} onToggleUser={toggleUser} invitationByUserId={invitationByUserId} emailByUserId={emailByUserId} handlers={handlers} />
          </div>
        ) : null}

        {visibleFirms.length === 0 && unassignedVisible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-surface px-6 py-12 text-center text-[14px] text-muted">{firms.length === 0 && users.length === 0 ? "No firms or users are visible for this account yet." : "No firms or members match your filters."}</div>
        ) : null}
      </div>

      {menuFirmId ? <button type="button" aria-hidden tabIndex={-1} className="fixed inset-0 z-40 cursor-default" onClick={() => setMenuFirmId(null)} /> : null}
    </Section>
  );
}

type UserStatusKind = "active" | "invited" | "suspended";

type UserRowHandlers = {
  onShowActivity: (user: User) => void;
  onInviteUser: (userId: string) => Promise<void>;
  onResetMfa: (user: User) => void;
  onEditUser: (user: User) => void;
  onSuspendUser: (user: User) => void;
  onRemoveUser: (user: User) => void;
};

function statusKind(user: User): UserStatusKind {
  if (!user.active) return "suspended";
  if (!user.lastSeenAt) return "invited";
  return "active";
}

function shortRole(user: User) {
  if (user.representativeType === "principal") return "Full member rep";
  if (user.representativeType === "alternate") return "Alternate rep";
  if (user.representativeType === "participant") return "Participant";
  return memberCategoryLabel(user.memberCategory ?? memberCategoryForUser(user));
}

function hasAdminBadge(user: User) {
  return isAdmin(user) || hasTechnicalRole(user, "superadministrator");
}

function lastSeenLabel(timestamp: number) {
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const FIRM_LOGO_COLORS = ["#7A1C2C", "#1c3a5e", "#2f5d3a", "#5b3a7c", "#8a5a16", "#1f5f63", "#7c2d12", "#3f3f7c"];

function FirmLogo({ firm }: { firm: Firm }) {
  const url = firm.logoUrl ?? firm.avatarUrl;
  if (url) return <img src={url} alt="" className="h-[38px] w-[38px] shrink-0 rounded-lg border border-line object-cover" />;
  const initials = firm.name.split(" ").map((part) => part[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
  let hash = 0;
  for (let index = 0; index < firm.id.length; index += 1) hash = (hash * 31 + firm.id.charCodeAt(index)) >>> 0;
  const color = FIRM_LOGO_COLORS[hash % FIRM_LOGO_COLORS.length];
  return <span style={{ backgroundColor: color }} className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-lg font-mono text-[12px] font-semibold text-white">{initials}</span>;
}

function TierBadge({ category }: { category: MembershipCategory }) {
  const full = category === "Full Member";
  return <span className={`rounded-[5px] border px-2 py-0.5 font-mono text-[10.5px] font-medium tracking-[0.01em] ${full ? "border-warnBorder bg-warnBg text-warn" : "border-line bg-divider text-muted"}`}>{category}</span>;
}

function StatusPill({ kind }: { kind: UserStatusKind }) {
  const map: Record<UserStatusKind, { tone: PillTone; label: string }> = {
    active: { tone: "good", label: "Active" },
    invited: { tone: "warn", label: "Invited" },
    suspended: { tone: "bad", label: "Suspended" },
  };
  const { tone, label } = map[kind];
  return <Pill tone={tone} dot>{label}</Pill>;
}

function SelectBox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <label className="inline-grid shrink-0 cursor-pointer place-items-center">
      <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
      <span className="grid h-[17px] w-[17px] place-items-center rounded-[5px] border-[1.5px] border-[#cfc9bb] bg-white text-transparent transition peer-checked:border-primary peer-checked:bg-primary peer-checked:text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>
    </label>
  );
}

function IconBtn({ icon: Icon, title, onClick, tone }: { icon: typeof Mail; title: string; onClick: () => void; tone?: "warn" | "danger" }) {
  const toneCls = tone === "danger" ? "hover:bg-badBg hover:text-bad" : tone === "warn" ? "hover:bg-warnBg hover:text-warn" : "hover:bg-divider hover:text-ink";
  return <button type="button" title={title} aria-label={title} onClick={onClick} className={`grid h-[30px] w-[30px] place-items-center rounded-md text-muted transition ${toneCls}`}><Icon className="h-4 w-4" /></button>;
}

function FirmMenuLink({ to, icon: Icon, label, onClick }: { to: string; icon: typeof Mail; label: string; onClick: () => void }) {
  return <Link to={to} onClick={onClick} className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium text-ink hover:bg-divider"><Icon className="h-4 w-4 text-muted" /> {label}</Link>;
}

function FirmMenuButton({ icon: Icon, label, onClick, tone }: { icon: typeof Mail; label: string; onClick: () => void; tone?: "warn" | "danger" }) {
  const toneCls = tone === "danger" ? "text-danger hover:bg-badBg" : tone === "warn" ? "text-warn hover:bg-warnBg" : "text-ink hover:bg-divider";
  const iconCls = tone ? "text-current" : "text-muted";
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium ${toneCls}`}><Icon className={`h-4 w-4 ${iconCls}`} /> {label}</button>;
}

function UserTable({ users, selected, onToggleUser, invitationByUserId, emailByUserId, handlers }: { users: User[]; selected: Set<string>; onToggleUser: (id: string) => void; invitationByUserId: Map<string, AdminInvitationRecord>; emailByUserId: Map<string, AdminEmailOutboxRecord>; handlers: UserRowHandlers }) {
  const th = "border-b border-line bg-surface px-3.5 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[0.09em] text-muted";
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-[38px] border-b border-line bg-surface px-3.5 py-2.5" />
            <th className={th}>Member</th>
            <th className={`${th} w-[160px]`}>Role</th>
            <th className={`${th} w-[200px]`}>Status</th>
            <th className={`${th} w-[110px]`}>Last active</th>
            <th className="border-b border-line bg-surface px-3.5 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <UserRow key={user.id} user={user} selected={selected.has(user.id)} onToggle={() => onToggleUser(user.id)} invitation={invitationByUserId.get(user.id)} email={emailByUserId.get(user.id)} handlers={handlers} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({ user, selected, onToggle, invitation, email, handlers }: { user: User; selected: boolean; onToggle: () => void; invitation?: AdminInvitationRecord; email?: AdminEmailOutboxRecord; handlers: UserRowHandlers }) {
  const kind = statusKind(user);
  const note = kind === "invited"
    ? `${invitation ? `Invite ${invitation.status}` : "Not invited"} · awaiting sign-in`
    : kind === "suspended" ? "Account suspended" : email ? `Mail ${email.status}` : "";
  const td = "px-3.5 py-3 align-middle";
  return (
    <tr className={`border-b border-divider transition last:border-b-0 hover:bg-hover ${selected ? "bg-hover" : ""}`}>
      <td className={td}><SelectBox checked={selected} onChange={onToggle} /></td>
      <td className={td}>
        <div className="flex items-center gap-2.5">
          <Avatar name={user.name} imageUrl={user.avatarUrl} size="sm" shape="rounded" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[13.5px] font-semibold text-ink">{user.name}</span>
              {hasAdminBadge(user) ? <span className="rounded border border-infoBorder bg-infoBg px-1.5 py-px font-mono text-[9.5px] font-medium uppercase tracking-[0.02em] text-navy">Admin</span> : null}
            </div>
            <div className="truncate text-[12.5px] text-muted">{user.email}</div>
          </div>
        </div>
      </td>
      <td className={`${td} text-[13px] font-medium text-ink`}>{shortRole(user)}</td>
      <td className={td}>
        <div className="flex flex-col gap-1">
          <StatusPill kind={kind} />
          {note ? <span className="text-[11px] text-muted">{note}</span> : null}
        </div>
      </td>
      <td className={`${td} text-[13px] tabular-nums text-ink`}>{user.lastSeenAt ? lastSeenLabel(user.lastSeenAt) : <span className="text-muted">Never</span>}</td>
      <td className={td}>
        <div className="flex items-center justify-end gap-1">
          <IconBtn icon={Activity} title="Activity" onClick={() => handlers.onShowActivity(user)} />
          <IconBtn icon={Send} title="Resend invite" onClick={() => { void handlers.onInviteUser(user.id); }} />
          <IconBtn icon={Pencil} title="Edit" onClick={() => handlers.onEditUser(user)} />
          <IconBtn icon={KeyRound} title="Reset MFA" onClick={() => handlers.onResetMfa(user)} />
          <span className="mx-1 h-[18px] w-px bg-line" />
          {user.active ? <IconBtn icon={Pause} title="Suspend" tone="warn" onClick={() => handlers.onSuspendUser(user)} /> : null}
          <IconBtn icon={Trash2} title="Remove" tone="danger" onClick={() => handlers.onRemoveUser(user)} />
        </div>
      </td>
    </tr>
  );
}

function FirmGovernanceControls({ firm, users, practiceAreas, selectedPracticeAreaIds, onSave }: { firm: Firm; users: User[]; practiceAreas: LexData["practiceAreas"]; selectedPracticeAreaIds: string[]; onSave: (input: { firmId: string; votingRepresentativeUserId?: string | null; alternateRepresentativeUserIds: string[]; practiceAreaIds: string[] }) => Promise<void> }) {
  const [votingRepresentativeUserId, setVotingRepresentativeUserId] = useState(firm.votingRepresentativeUserId ?? "");
  const [alternateRepresentativeUserIds, setAlternateRepresentativeUserIds] = useState<string[]>(firm.alternateRepresentativeUserIds ?? []);
  const [practiceAreaIds, setPracticeAreaIds] = useState(selectedPracticeAreaIds);
  const [saving, setSaving] = useState(false);
  const toggle = (id: string, current: string[], setValue: (value: string[]) => void) => {
    setValue(current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };
  return (
    <div className="mt-4 rounded-lg border border-line bg-surfaceAlt p-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <Select label="Voting representative" values={["", ...users.map((user) => user.name)]} value={users.find((user) => user.id === votingRepresentativeUserId)?.name ?? ""} onChange={(name) => setVotingRepresentativeUserId(users.find((user) => user.name === name)?.id ?? "")} />
          <div className="mt-3">
            <div className="mb-2 text-xs font-medium text-muted">Alternate representatives</div>
            <div className="grid gap-2">
              {users.map((user) => (
                <label key={user.id} className="flex min-h-9 items-center gap-2 rounded-lg border border-divider bg-canvas px-3 text-sm">
                  <input type="checkbox" checked={alternateRepresentativeUserIds.includes(user.id)} onChange={() => toggle(user.id, alternateRepresentativeUserIds, setAlternateRepresentativeUserIds)} />
                  {user.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="mb-2 text-xs font-medium text-muted">Firm practice areas</div>
          <div className="grid max-h-72 gap-2 overflow-auto pr-1">
            {practiceAreas.map((area) => (
              <label key={area.id} className="flex min-h-9 items-center gap-2 rounded-lg border border-divider bg-canvas px-3 text-sm">
                <input type="checkbox" checked={practiceAreaIds.includes(area.id)} onChange={() => toggle(area.id, practiceAreaIds, setPracticeAreaIds)} />
                {area.name}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Button type="button" disabled={saving} onClick={async () => {
          setSaving(true);
          try {
            await onSave({ firmId: firm.id, votingRepresentativeUserId: votingRepresentativeUserId || null, alternateRepresentativeUserIds, practiceAreaIds });
          } finally {
            setSaving(false);
          }
        }}>{saving ? "Saving..." : "Save representatives and practices"}</Button>
      </div>
    </div>
  );
}

function SourceIconButton({ title, onClick, children, danger, disabled }: { title: string; onClick: () => void; children: ReactNode; danger?: boolean; disabled?: boolean }) {
  return (
    <button type="button" title={title} aria-label={title} disabled={disabled} onClick={onClick} className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-surface text-secondary transition hover:bg-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 ${danger ? "hover:border-bad/40 hover:text-bad" : "hover:text-ink"}`}>
      {children}
    </button>
  );
}

function LegislativeSourcesPanel({ data, persistence, onNotice }: { data: LexData; persistence?: AdminPersistence; onNotice: (message: string) => void }) {
  const sources = (data.legislativeSourceDefinitions ?? []).slice().sort((a, b) => a.jurisdiction.localeCompare(b.jurisdiction) || a.label.localeCompare(b.label));
  const queue = (data.legislativeReviewQueue ?? []).filter((review) => review.status === "pending");
  const jurisdictions = ["EU", "Luxembourg", "France", "Belgium", "Switzerland"];
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState(false);
  const statusTone = (status?: string) => (status === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : status === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : status === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-line bg-surface text-secondary");

  type SourceRow = (typeof sources)[number];
  const toggleActive = async (source: SourceRow) => {
    try { await persistence?.setLegislativeSourceActive?.({ sourceDefinitionId: source.id, active: !source.active }); onNotice(`${source.label} ${source.active ? "disabled" : "enabled"}.`); }
    catch (error) { onNotice(`Could not update source: ${errorMessage(error)}`); }
  };
  const startEdit = (source: SourceRow) => { setConfirmDeleteId(null); setEditingSourceId(source.id); setEditLabel(source.label); setEditUrl(source.normalizedUrl); };
  const saveEdit = async (source: SourceRow) => {
    setRowBusy(true);
    try {
      await persistence?.updateLegislativeSource?.({ sourceDefinitionId: source.id, label: editLabel.trim() || undefined, url: editUrl.trim() || undefined });
      onNotice(`${editLabel.trim() || source.label} updated.`);
      setEditingSourceId(null);
    } catch (error) { onNotice(`Could not update source: ${errorMessage(error)}`); }
    finally { setRowBusy(false); }
  };
  const doDelete = async (source: SourceRow) => {
    setRowBusy(true);
    try {
      await persistence?.deleteLegislativeSource?.({ sourceDefinitionId: source.id });
      onNotice(`${source.label} deleted.`);
      setConfirmDeleteId(null);
    } catch (error) { onNotice(`Could not delete source: ${errorMessage(error)}`); }
    finally { setRowBusy(false); }
  };

  return (
    <Section title="Legislative sources & review" kicker="Legislative news">
      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Label>Refresh now</Label>
          {jurisdictions.map((jurisdiction) => (
            <Button key={jurisdiction} type="button" variant="secondary" disabled={refreshing !== null} onClick={async () => {
              setRefreshing(jurisdiction);
              try { await persistence?.refreshLegislativeJurisdiction?.(jurisdiction); onNotice(`Refreshed ${jurisdiction} legislative sources.`); }
              catch (error) { onNotice(`Refresh failed: ${errorMessage(error)}`); }
              finally { setRefreshing(null); }
            }}>{refreshing === jurisdiction ? "Refreshing…" : jurisdiction}</Button>
          ))}
        </div>
        <div className="mt-4 grid gap-2 border-t border-divider pt-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
          <label><span className="mb-1 block text-xs font-medium text-muted">Add a source URL</span><input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-sm" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://… portal, RSS/Atom or open-data API" /></label>
          <label><span className="mb-1 block text-xs font-medium text-muted">Label (optional)</span><input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-sm" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="e.g. EUR-Lex consultations" /></label>
          <Button type="button" disabled={busy || !url.trim()} onClick={async () => {
            setBusy(true); setResult(null);
            try { const outcome = await persistence?.suggestLegislativeSource?.({ url, label: label || undefined }); setResult(outcome?.summary ?? "Submitted."); if (outcome?.accepted) { setUrl(""); setLabel(""); } }
            catch (error) { setResult(errorMessage(error)); }
            finally { setBusy(false); }
          }}>{busy ? "Checking…" : "Add source"}</Button>
        </div>
        {result ? <div className="mt-2 text-sm text-secondary">{result}</div> : null}
      </Card>

      <div className="mb-2 flex items-center justify-between">
        <Label>Configured sources ({sources.length})</Label>
      </div>
      <div className="grid gap-2">
        {sources.length > 0 ? [...sources].sort((a, b) => Number(b.active) - Number(a.active)).map((source) => (
          <Card key={source.id} className={`flex flex-wrap items-center gap-3 ${source.active ? "" : "opacity-60"}`}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink">{source.label}</span>
                <Tag>{source.jurisdiction}</Tag>
                <Tag>{source.sourceType.replace(/_/g, " ")}</Tag>
                <Tag>{source.fetchMode.toUpperCase()}</Tag>
                {source.requiresCredentials ? <Tag>credentials</Tag> : null}
                {source.active ? null : <Tag>disabled</Tag>}
              </div>
              <div className="mt-1 truncate text-xs text-muted">{source.normalizedUrl}</div>
              {source.lastStatusMessage ? <div className="mt-1 text-xs text-secondary">{source.lastStatusMessage}</div> : null}
            </div>
            {editingSourceId === source.id ? (
              <div className="flex w-full flex-wrap items-end gap-2 border-t border-divider pt-3">
                <label className="min-w-[150px] flex-1"><span className="mb-1 block text-xs font-medium text-muted">Label</span><input className="min-h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm" value={editLabel} onChange={(event) => setEditLabel(event.target.value)} /></label>
                <label className="min-w-[220px] flex-[2]"><span className="mb-1 block text-xs font-medium text-muted">Source URL</span><input className="min-h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm" value={editUrl} onChange={(event) => setEditUrl(event.target.value)} placeholder="https://…" /></label>
                <SourceIconButton title="Save changes" disabled={rowBusy} onClick={() => void saveEdit(source)}><Check className="h-4 w-4" /></SourceIconButton>
                <SourceIconButton title="Cancel" disabled={rowBusy} onClick={() => setEditingSourceId(null)}><X className="h-4 w-4" /></SourceIconButton>
              </div>
            ) : confirmDeleteId === source.id ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-secondary">Delete this source?</span>
                <SourceIconButton title="Confirm delete" danger disabled={rowBusy} onClick={() => void doDelete(source)}><Check className="h-4 w-4" /></SourceIconButton>
                <SourceIconButton title="Cancel" disabled={rowBusy} onClick={() => setConfirmDeleteId(null)}><X className="h-4 w-4" /></SourceIconButton>
              </div>
            ) : (
              <>
                {source.active ? <span className={`mono rounded-md border px-2.5 py-1 text-[11px] font-medium ${statusTone(source.lastStatus)}`}>{source.lastStatus ?? "never"}</span> : null}
                <div className="flex items-center gap-1.5">
                  <SourceIconButton title="Edit source" onClick={() => startEdit(source)}><Pencil className="h-4 w-4" /></SourceIconButton>
                  <SourceIconButton title={source.active ? "Disable source" : "Enable source"} onClick={() => void toggleActive(source)}>{source.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}</SourceIconButton>
                  <SourceIconButton title="Delete source" danger onClick={() => { setEditingSourceId(null); setConfirmDeleteId(source.id); }}><Trash2 className="h-4 w-4" /></SourceIconButton>
                </div>
              </>
            )}
          </Card>
        )) : <Card><p className="text-sm text-secondary">No sources configured yet. The default sources seed on the first scheduled refresh, or add one above.</p></Card>}
      </div>

      <div className="mb-2 mt-5 flex items-center justify-between">
        <Label>Review queue ({queue.length})</Label>
      </div>
      <div className="grid gap-2">
        {queue.length > 0 ? queue.map((review) => (
          <Card key={review.id} className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-ink">{review.snapshotTitle ?? "Legislative item"}</div>
              <div className="mt-0.5 text-xs text-muted">{review.snapshotSourceLabel ?? "Source"}{review.snapshotJurisdiction ? ` · ${review.snapshotJurisdiction}` : ""}</div>
              {review.snapshotExcerpt ? <p className="mt-1 text-sm leading-6 text-secondary">{review.snapshotExcerpt}</p> : null}
              {review.snapshotSourceUrl ? <a className="text-xs font-medium text-navy hover:underline" href={review.snapshotSourceUrl} target="_blank" rel="noreferrer">Open source</a> : null}
            </div>
            <div className="flex gap-2">
              <Button type="button" onClick={async () => { try { await persistence?.reviewLegislativeSnapshot?.({ reviewId: review.id, status: "approved" }); onNotice("Item approved and published to the feed."); } catch (error) { onNotice(`Could not approve: ${errorMessage(error)}`); } }}>Approve</Button>
              <Button type="button" variant="secondary" onClick={async () => { try { await persistence?.reviewLegislativeSnapshot?.({ reviewId: review.id, status: "rejected" }); onNotice("Item hidden from the feed."); } catch (error) { onNotice(`Could not reject: ${errorMessage(error)}`); } }}>Hide</Button>
            </div>
          </Card>
        )) : <Card><p className="text-sm text-secondary">Nothing awaiting review.</p></Card>}
      </div>
    </Section>
  );
}

function PracticeAreasPanel({
  data,
  practiceAreas,
  inactiveAreas,
  onAdd,
  onEdit,
  onCreateRecommended,
  onCreateMissingRecommended,
  onToggle,
}: {
  data: LexData;
  practiceAreas: LexData["practiceAreas"];
  inactiveAreas: number;
  onAdd: () => void;
  onEdit: (area: LexData["practiceAreas"][number]) => void;
  onCreateRecommended: (definition: PracticeAreaDefinition) => Promise<void>;
  onCreateMissingRecommended: (definitions: PracticeAreaDefinition[]) => Promise<void>;
  onToggle: (areaId: string) => void;
}) {
  const existingSlugs = new Set(practiceAreas.map((area) => area.slug));
  const missingRecommended = lexmosaicPracticeAreaCatalog.filter((area) => !existingSlugs.has(area.slug));
  return (
    <Section title="Practice areas" kicker="Admin managed" aside={<Button type="button" onClick={onAdd}>Add practice area</Button>}>
      <Card className="mb-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Metric label="Active" value={practiceAreas.length - inactiveAreas} />
          <Metric label="Inactive" value={inactiveAreas} />
          <Metric label="Firm links" value={data.firmPracticeAreas.length} />
        </div>
      </Card>
      <Card className="mb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-semibold">LexMosaic IP&IT recommended coverage</div>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-secondary">A focused practice-area set for a network of IP and technology lawyers, with granular coverage across AI, fintech, crypto-assets, digital regulation, cybersecurity, data, trade marks, domain names, patent litigation and copyright.</p>
          </div>
          {missingRecommended.length > 0 ? <Button type="button" variant="secondary" onClick={() => void onCreateMissingRecommended(missingRecommended)}>Add all missing</Button> : <Tag>complete</Tag>}
        </div>
        <div className="mt-4 grid gap-2 md:grid-cols-2">
          {lexmosaicPracticeAreaCatalog.map((definition) => {
            const exists = existingSlugs.has(definition.slug);
            return (
              <div key={definition.slug} className="rounded-lg border border-divider bg-canvas p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink">{definition.name}</div>
                    <p className="mt-1 text-xs leading-5 text-secondary">{definition.description}</p>
                  </div>
                  {exists ? <Tag>added</Tag> : <Button type="button" variant="secondary" className="shrink-0" onClick={() => void onCreateRecommended(definition)}>Add</Button>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      <div className="grid gap-3 md:grid-cols-2">
        {practiceAreas.map((area) => (
          <Card key={area.id}>
            <div className="flex items-start gap-3">
              <ListChecks className="mt-1 h-5 w-5 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{area.name}</div>
                <p className="mt-1 text-sm text-secondary">{area.description}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Tag>{area.slug}</Tag>
                  <Tag>{data.firmPracticeAreas.filter((link) => link.practiceAreaId === area.id).length} firms</Tag>
                  <Tag>{area.active ? "active" : "inactive"}</Tag>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" onClick={() => onEdit(area)}>Edit</Button>
                <Button type="button" variant="secondary" onClick={() => onToggle(area.id)}>{area.active ? "Deactivate" : "Reactivate"}</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  );
}

function ClassificationPanel({
  topicTags,
  jurisdictionTags,
  defaultConfidentiality,
  onAddTopic,
  onToggleTopic,
  onMergeTopic,
  onAddJurisdiction,
  onToggleJurisdiction,
  onDefaultConfidentiality,
}: {
  topicTags: { name: string; active: boolean; suggested: boolean }[];
  jurisdictionTags: { name: string; active: boolean }[];
  defaultConfidentiality: ConfidentialityLevel;
  onAddTopic: () => void;
  onToggleTopic: (name: string) => void;
  onMergeTopic: (name: string) => void;
  onAddJurisdiction: () => void;
  onToggleJurisdiction: (name: string) => void;
  onDefaultConfidentiality: (value: ConfidentialityLevel) => void;
}) {
  const [topicSearch, setTopicSearch] = useState("");
  const normalizedTopicSearch = topicSearch.trim().toLowerCase();
  const visibleTopicTags = normalizedTopicSearch
    ? topicTags.filter((tag) => `${tag.name} ${tag.active ? "approved" : "archived"} ${tag.suggested ? "suggested" : ""}`.toLowerCase().includes(normalizedTopicSearch))
    : topicTags;
  return (
    <div className="grid gap-5">
      <Section title="Post classification" kicker="Separate dimensions">
        <Card>
          <p className="text-sm leading-6 text-secondary">
            Post type, topic tags, jurisdiction tags, confidentiality, workflow status and action labels are separate fields. A post can be a Question about Data protection in the EU, but Data protection is never a post type.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <Metric label="Post types" value={postTypes.length} />
            <Metric label="Topic tags" value={topicTags.length} />
            <Metric label="Jurisdictions" value={jurisdictionTags.length} />
          </div>
        </Card>
      </Section>

      <Section title="Topic tags" kicker="Admin managed" aside={<Button type="button" onClick={onAddTopic}>Suggest topic tag</Button>}>
        <Card className="p-3">
          <div className="mb-3 grid gap-3 md:grid-cols-[minmax(220px,360px)_minmax(0,1fr)] md:items-center">
            <label>
              <span className="sr-only">Search topic tags</span>
              <input
                className="min-h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm"
                value={topicSearch}
                onChange={(event) => setTopicSearch(event.target.value)}
                placeholder="Search topic tags..."
              />
            </label>
            <div className="flex flex-wrap gap-2 text-xs text-secondary">
              <Tag>{visibleTopicTags.length} shown</Tag>
              <Tag>{topicTags.filter((tag) => tag.active).length} approved</Tag>
              <Tag>{topicTags.filter((tag) => !tag.active).length} archived</Tag>
              <Tag>{topicTags.filter((tag) => tag.suggested).length} suggested</Tag>
            </div>
          </div>
          <div className="max-h-[360px] overflow-auto rounded-lg border border-divider bg-canvas p-2">
            <div className="flex flex-wrap gap-2">
              {visibleTopicTags.map((tag) => (
                <div
                  key={tag.name}
                  className={`inline-flex min-h-9 max-w-full items-center gap-2 rounded-full border px-2.5 py-1 text-sm ${
                    tag.active ? "border-line bg-surface text-ink" : "border-divider bg-app text-muted"
                  }`}
                >
                  <span className="max-w-[220px] truncate font-medium">{tag.name}</span>
                  {tag.suggested ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-800">new</span> : null}
                  <button type="button" className="rounded-full px-1.5 text-[11px] font-semibold text-primary hover:bg-primary/10" onClick={() => onToggleTopic(tag.name)}>
                    {tag.active ? "Archive" : "Approve"}
                  </button>
                  <button type="button" className="rounded-full px-1.5 text-[11px] font-semibold text-secondary hover:bg-hover hover:text-primary" onClick={() => onMergeTopic(tag.name)}>
                    Merge
                  </button>
                </div>
              ))}
              {visibleTopicTags.length === 0 ? <p className="px-2 py-3 text-sm text-secondary">No topic tags match this search.</p> : null}
            </div>
          </div>
        </Card>
      </Section>

      <Section title="Jurisdiction tags" kicker="Legal scope" aside={<Button type="button" onClick={onAddJurisdiction}>Create jurisdiction</Button>}>
        <div className="flex flex-wrap gap-2">
          {jurisdictionTags.map((tag) => (
            <button key={tag.name} type="button" className={`min-h-10 rounded-lg border px-3 text-sm font-medium ${tag.active ? "border-line bg-surface text-ink" : "border-divider bg-canvas text-muted"}`} onClick={() => onToggleJurisdiction(tag.name)}>
              {tag.name}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Confidentiality defaults" kicker="Visibility">
        <Card>
          <Select label="Default confidentiality level" values={confidentialityLevels} value={defaultConfidentiality} onChange={(value) => onDefaultConfidentiality(value as ConfidentialityLevel)} />
          <p className="mt-3 text-sm leading-6 text-secondary">New posts default to Network-wide unless the selected post type or working group rules require a narrower level.</p>
        </Card>
      </Section>
    </div>
  );
}

function MembershipPanel({ configs, firms, onConfigure }: { configs: MembershipCategoryConfig[]; firms: Firm[]; onConfigure: (config: MembershipCategoryConfig) => void }) {
  return (
    <Section title="Membership categories" kicker="Governance and access">
      <Card className="mb-3">
        <p className="text-sm leading-6 text-secondary">
          Categories describe what a firm or participant is allowed to do in Lexmosaic. They affect access, voting profile, quorum treatment and whether the member can give or receive procurations.
        </p>
      </Card>
      <div className="grid gap-3 md:grid-cols-2">
        {configs.map((config) => (
          <Card key={config.category} className="p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-1 h-5 w-5 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-semibold text-ink">{config.category}</div>
                  <Tag>{firms.filter((firm) => firm.membershipCategory === config.category).length} firms</Tag>
                  <Tag>{config.active ? "active" : "inactive"}</Tag>
                </div>
                <p className="mt-2 text-sm leading-6 text-secondary">{config.explanation}</p>
                <div className="mt-3 grid gap-2 text-sm">
                  <InfoLine label="Access" value={config.accessSummary} />
                  <InfoLine label="Voting" value={config.votingSummary} />
                </div>
              </div>
              <Button type="button" variant="secondary" onClick={() => onConfigure(config)}>Edit</Button>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  );
}

function AffiliationPanel({ groups, firms, onCreate, onEdit }: { groups: AffiliationGroup[]; firms: Firm[]; onCreate: () => void; onEdit: (group: AffiliationGroup) => void }) {
  return (
    <Section title="Affiliation groups" kicker="Restricted visibility" aside={<Button type="button" onClick={onCreate}>Create group</Button>}>
      <Card className="mb-3">
        <p className="text-sm leading-6 text-secondary">
          Affiliation groups record structural links between firms, such as common ownership, a shared brand or a formal alliance. They are visible only to authorised membership and governance users.
        </p>
      </Card>
      <div className="grid gap-3 md:grid-cols-2">
        {groups.length > 0 ? groups.map((group) => (
          <Card key={group.id}>
            <div className="flex items-start gap-3">
              <Building2 className="mt-1 h-5 w-5 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{group.name}</div>
                <p className="mt-2 text-sm leading-6 text-secondary">{group.explanation}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Tag>{group.affiliationType}</Tag>
                  <Tag>{group.active ? "active" : "inactive"}</Tag>
                  <Tag>{firms.filter((firm) => firm.affiliationGroupId === group.id).length} firms</Tag>
                </div>
              </div>
              <Button type="button" variant="secondary" onClick={() => onEdit(group)}>Edit</Button>
            </div>
          </Card>
        )) : <Card>No affiliation groups visible for this account.</Card>}
      </div>
    </Section>
  );
}

function GovernancePanel({ settings, onSave, onOpenNote }: { settings: GovernanceSettings; onSave: (settings: GovernanceSettings) => void; onOpenNote: () => void }) {
  const [draft, setDraft] = useState(settings);
  return (
    <Section
      title="Governance settings"
      kicker="Structured controls"
      aside={(
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={onOpenNote}>Edit governance settings</Button>
          <Button type="button" onClick={() => onSave(draft)}>Save governance settings</Button>
        </div>
      )}
    >
      <Card className="mb-3">
        <p className="text-sm leading-6 text-secondary">These settings define the default voting model used by governance workflows. They are stored as structured admin settings and written to the audit log when saved.</p>
      </Card>
      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField label="Base firm weight" value={draft.baseFirmWeight} step="0.05" onChange={(value) => setDraft((current) => ({ ...current, baseFirmWeight: value }))} />
            <NumberField label="Maximum firm weight" value={draft.maxFirmWeight} step="0.05" onChange={(value) => setDraft((current) => ({ ...current, maxFirmWeight: value }))} />
            <NumberField label="Quorum threshold" value={draft.quorumThreshold} step="0.01" onChange={(value) => setDraft((current) => ({ ...current, quorumThreshold: value }))} />
            <NumberField label="Majority threshold" value={draft.majorityThreshold} step="0.01" onChange={(value) => setDraft((current) => ({ ...current, majorityThreshold: value }))} />
            <NumberField label="Procuration cap" value={draft.procurationCap} step="1" onChange={(value) => setDraft((current) => ({ ...current, procurationCap: value }))} />
          </div>
        </Card>
        <Card>
          <label>
            <span className="mb-1 block text-xs font-medium text-muted">Sensitive decision rule</span>
            <textarea className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2" value={draft.sensitiveDecisionRule} onChange={(event) => setDraft((current) => ({ ...current, sensitiveDecisionRule: event.target.value }))} />
          </label>
          <div className="mt-3 grid gap-2">
            <label className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm">
              <input type="checkbox" checked={draft.secretBallotDefault} onChange={(event) => setDraft((current) => ({ ...current, secretBallotDefault: event.target.checked }))} />
              Secret ballot by default for sensitive votes
            </label>
            <label className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm">
              <input type="checkbox" checked={draft.participationWeightingEnabled} onChange={(event) => setDraft((current) => ({ ...current, participationWeightingEnabled: event.target.checked }))} />
              Participation weighting enabled
            </label>
          </div>
        </Card>
      </div>
    </Section>
  );
}

function ReportsShortcut() {
  return (
    <Section title="Reports and exports" kicker="One builder">
      <Card>
        <FileText className="mb-3 h-5 w-5 text-primary" />
        <div className="font-semibold">Use the report builder</div>
        <p className="mt-2 text-sm text-secondary">Build member, voting, procuration, cooperation, legislation and audit exports from one place.</p>
        <div className="mt-4"><Link className="inline-flex min-h-11 items-center rounded-lg bg-primary px-4 text-sm font-semibold text-white" to="/admin/reports">Open reports</Link></div>
      </Card>
    </Section>
  );
}

function AuditPanel({ data, auditLogs, onOpen }: { data: LexData; auditLogs?: AuditRecord[]; onOpen: (record: AuditRecord) => void }) {
  const [filter, setFilter] = useState("All");
  const records = (auditLogs ?? data.activityEvents.map((event): AuditRecord => ({
    id: event.id,
    actionType: event.type,
    entityType: event.targetType,
    entityId: event.targetId,
    actor: data.users.find((user) => user.id === event.actorUserId)?.name ?? "System",
    createdAt: event.createdAt,
    afterJson: JSON.stringify({ title: event.title, summary: event.summary, visibility: event.visibility }, null, 2),
  }))).map((record) => ({ ...record, actor: record.actor ?? data.users.find((user) => user.id === record.actorUserId)?.name ?? "System" }));
  const entityTypes = ["All", ...Array.from(new Set(records.map((record) => record.entityType)))];
  const visibleRecords = filter === "All" ? records : records.filter((record) => record.entityType === filter);
  function exportCsv() {
    const csv = ["Date,Actor,Action,Entity,Entity ID", ...visibleRecords.map((record) => [new Date(record.createdAt).toISOString(), record.actor, record.actionType, record.entityType, record.entityId].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))].join("\n");
    void navigator.clipboard?.writeText(csv);
  }
  return (
    <Section title="Audit log" kicker="Filtered activity" aside={<Button type="button" variant="secondary" onClick={exportCsv}>Copy CSV</Button>}>
      <Card className="mb-3">
        <div className="grid gap-3 sm:grid-cols-[260px_minmax(0,1fr)] sm:items-end">
          <Select label="Entity type" values={entityTypes} value={filter} onChange={setFilter} />
          <p className="text-sm text-secondary">Open a row to inspect the recorded details. Use Copy CSV for a quick admin export.</p>
        </div>
      </Card>
      <div className="grid gap-2">
        {visibleRecords.slice(0, 20).map((record) => (
          <button key={record.id} type="button" className="grid gap-2 rounded-lg border border-line bg-surface px-4 py-3 text-left hover:bg-hover md:grid-cols-[170px_170px_minmax(0,1fr)_140px]" onClick={() => onOpen(record)}>
            <span className="text-sm font-medium text-ink">{record.actionType}</span>
            <span className="text-sm text-secondary">{record.entityType}</span>
            <span className="truncate text-sm text-secondary">{record.actor}</span>
            <span className="text-xs text-muted">{new Date(record.createdAt).toLocaleString()}</span>
          </button>
        ))}
      </div>
    </Section>
  );
}

function SystemHealth({ data, firms, users, adminOverview, publicSiteData, onBackfill, onRefresh }: { data: LexData; firms: Firm[]; users: User[]; adminOverview?: AdminOverview; publicSiteData?: PublicSiteAdminData; onBackfill: () => Promise<void>; onRefresh: () => void }) {
  const failedOperations = adminOverview?.auditLogs.filter((log) => /fail|error|reject/i.test(`${log.actionType} ${log.afterJson ?? ""}`)).length ?? data.notifications.filter((notification) => /failed|error|declined/i.test(`${notification.type} ${notification.title} ${notification.body}`)).length;
  const pendingPublicApprovals = publicSiteData?.changes.filter((change) => change.status === "pending").length ?? 0;
  const lastReport = adminOverview?.reports.slice().sort((a, b) => b.createdAt - a.createdAt)[0];
  const lastAudit = adminOverview?.auditLogs.slice().sort((a, b) => b.createdAt - a.createdAt)[0];
  return (
    <Section title="System health" aside={<div className="flex flex-wrap gap-2"><Button type="button" variant="secondary" onClick={() => void onBackfill()}>Backfill member access</Button><Button type="button" variant="secondary" onClick={onRefresh}>Refresh</Button></div>}>
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <div>
            <Label>Active backend</Label>
            <div className="mt-1 font-medium">Convex connected through the private app runtime</div>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <Metric label="Firms" value={firms.length} />
          <Metric label="Users" value={users.length} />
          <Metric label="Notifications" value={data.notifications.length} />
          <Metric label="Failed ops" value={failedOperations} />
          <Metric label="Public approvals" value={pendingPublicApprovals} />
          <Metric label="Latest build" value="loaded" />
        </div>
        <div className="mt-4 rounded-lg border border-divider bg-canvas px-3 py-2 text-sm text-secondary">
          {lastAudit ? `Last admin action: ${lastAudit.actionType} on ${new Date(lastAudit.createdAt).toLocaleString()}.` : "No admin audit records loaded."}
          {lastReport ? ` Last report: ${lastReport.type} on ${new Date(lastReport.createdAt).toLocaleString()}.` : ""}
        </div>
      </Card>
    </Section>
  );
}

function AdminDangerDialog({
  title,
  description,
  confirmLabel,
  defaultReason,
  requireTypedValue,
  warning,
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  defaultReason: string;
  requireTypedValue?: string;
  warning?: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState(defaultReason);
  const [typedValue, setTypedValue] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canConfirm = !requireTypedValue || typedValue.trim() === requireTypedValue;
  const submit = async () => {
    if (!canConfirm) {
      setError(`Type ${requireTypedValue} to confirm.`);
      return;
    }
    setError(null);
    setWorking(true);
    try {
      await onConfirm(reason.trim() || defaultReason);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setWorking(false);
    }
  };

  return (
    <ActionModal title={title} description={description} confirmLabel={working ? "Working..." : confirmLabel} onClose={onClose} onConfirm={working ? undefined : () => void submit()}>
      <div className="grid gap-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-900">
          {warning ?? "Suspension blocks private workspace access. Removal is permanent and should only be used when the member record must be deleted."}
        </div>
        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div> : null}
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Admin reason</span>
          <textarea className="min-h-20 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm" value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        {requireTypedValue ? (
          <label>
            <span className="mb-1 block text-xs font-medium text-muted">Type exactly to confirm: {requireTypedValue}</span>
            <input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-sm" value={typedValue} onChange={(event) => setTypedValue(event.target.value)} />
          </label>
        ) : null}
      </div>
    </ActionModal>
  );
}

function FirmEditor({ firm, practiceAreas, selectedPracticeAreaIds, onUploadLogo, onClose, onSave }: { firm?: Firm; practiceAreas: LexData["practiceAreas"]; selectedPracticeAreaIds: string[]; onUploadLogo?: (file: File) => Promise<string>; onClose: () => void; onSave: (firm: Firm, practiceAreaIds: string[]) => void | Promise<void> }) {
  const [draft, setDraft] = useState<Firm>(firm ?? {
    id: `local-firm-${Date.now()}`,
    name: "",
    jurisdiction: "Luxembourg",
    address: null,
    websiteUrl: null,
    telephone: null,
    contactEmail: null,
    logoUrl: null,
    avatarUrl: null,
    membershipCategory: "Full Member",
    lawyerCount: 1,
    independenceStatement: "",
    status: "active",
    affiliationGroupId: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  const [practiceAreaIds, setPracticeAreaIds] = useState<string[]>(selectedPracticeAreaIds);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoRef = useRef<HTMLInputElement>(null);
  const onLogoSelected = (file: File | null) => {
    setLogoFile(file);
    if (!file) {
      setLogoPreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  };
  const togglePracticeArea = (areaId: string) => {
    setPracticeAreaIds((current) => current.includes(areaId) ? current.filter((id) => id !== areaId) : [...current, areaId]);
  };
  const saveFirm = async () => {
    if (!draft.name.trim()) {
      setError("Firm name is required.");
      return;
    }
    if (!draft.jurisdiction.trim()) {
      setError("Jurisdiction is required.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      let logoStorageId = draft.logoStorageId ?? undefined;
      if (logoFile && onUploadLogo) logoStorageId = await onUploadLogo(logoFile);
      await onSave({ ...draft, name: draft.name.trim(), jurisdiction: draft.jurisdiction.trim(), logoStorageId, updatedAt: Date.now() }, practiceAreaIds);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ActionModal title={firm ? "Edit firm" : "Add firm"} description={draft.name || "Create a member firm record"} confirmLabel={saving ? "Saving..." : "Save firm"} onClose={onClose} onConfirm={saving ? undefined : saveFirm}>
      <div className="grid gap-3">
        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div> : null}
        <Field label="Firm name" value={draft.name} onChange={(value) => setDraft((current) => ({ ...current, name: value }))} />
        <JurisdictionSelect value={draft.jurisdiction} onChange={(value) => setDraft((current) => ({ ...current, jurisdiction: value }))} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Full firm address</span>
          <textarea className="min-h-24 w-full rounded-lg border border-line bg-surface px-3 py-2" value={draft.address ?? ""} onChange={(event) => setDraft((current) => ({ ...current, address: event.target.value }))} />
        </label>
        <Field label="Website link" type="url" value={draft.websiteUrl ?? ""} onChange={(value) => setDraft((current) => ({ ...current, websiteUrl: value }))} />
        <div>
          <span className="mb-1 block text-xs font-medium text-muted">Firm logo</span>
          <div className="flex items-center gap-3">
            <Avatar name={draft.name || "Firm"} imageUrl={logoPreview ?? draft.logoUrl ?? null} shape="rounded" />
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(event) => onLogoSelected(event.target.files?.[0] ?? null)} />
            <Button type="button" variant="secondary" onClick={() => logoRef.current?.click()} disabled={!onUploadLogo}>{logoFile ? `Selected: ${logoFile.name}` : "Upload logo"}</Button>
          </div>
          <p className="mt-1 text-[11px] text-muted">{onUploadLogo ? "This logo represents the firm across the workspace." : "Direct logo upload is available in the live workspace."}</p>
        </div>
        <Field label="Telephone" type="tel" value={draft.telephone ?? ""} onChange={(value) => setDraft((current) => ({ ...current, telephone: value }))} />
        <Field label="Firm email" type="email" value={draft.contactEmail ?? ""} onChange={(value) => setDraft((current) => ({ ...current, contactEmail: value }))} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Practice areas</span>
          <div className="grid max-h-48 gap-2 overflow-auto rounded-lg border border-line bg-surface p-3 sm:grid-cols-2">
            {practiceAreas.filter((area) => area.active).map((area) => (
              <label key={area.id} className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-secondary hover:bg-hover">
                <input type="checkbox" checked={practiceAreaIds.includes(area.id)} onChange={() => togglePracticeArea(area.id)} />
                <span>{area.name}</span>
              </label>
            ))}
          </div>
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Membership category</span>
          <select className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={draft.membershipCategory} onChange={(event) => setDraft((current) => ({ ...current, membershipCategory: event.target.value as MembershipCategory }))}>
            {membershipCategories.map((category) => <option key={category}>{category}</option>)}
          </select>
        </label>
        <Field label="Lawyer count" type="number" value={String(draft.lawyerCount)} onChange={(value) => setDraft((current) => ({ ...current, lawyerCount: Number(value) || 0 }))} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Firm profile description</span>
          <textarea className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2" value={draft.independenceStatement} onChange={(event) => setDraft((current) => ({ ...current, independenceStatement: event.target.value }))} />
        </label>
      </div>
    </ActionModal>
  );
}

function UserActivityModal({ user, load, onClose }: { user: User; load?: (userId: string) => Promise<UserActivity>; onClose: () => void }) {
  const [activity, setActivity] = useState<UserActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!load) return;
    setLoading(true);
    load(user.id)
      .then(setActivity)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Could not load activity."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const merged = activity
    ? [
        ...activity.activityEvents.map((event) => ({ id: event.id, when: event.createdAt, label: event.title, detail: `${event.type}${event.summary ? ` — ${event.summary}` : ""}` })),
        ...activity.auditLogs.map((log) => ({ id: log.id, when: log.createdAt, label: log.actionType, detail: `${log.entityType} ${log.entityId}` })),
      ].sort((a, b) => b.when - a.when)
    : [];
  const lastSeen = activity?.lastSeenAt ?? user.lastSeenAt ?? null;
  return (
    <ActionModal title={`Activity — ${user.name}`} description={user.email} confirmLabel="Close" onClose={onClose} onConfirm={onClose}>
      <div className="grid gap-3">
        <div className="rounded-lg border border-divider bg-canvas px-3 py-2 text-sm">
          <span className="text-muted">Last signed in: </span>{lastSeen ? new Date(lastSeen).toLocaleString() : "never"}
        </div>
        {!load ? (
          <p className="text-sm text-secondary">User activity is available in the live workspace.</p>
        ) : loading ? (
          <p className="text-sm text-secondary">Loading activity…</p>
        ) : error ? (
          <p className="text-sm text-rose-700">{error}</p>
        ) : merged.length === 0 ? (
          <p className="text-sm text-secondary">No recorded actions for this user yet.</p>
        ) : (
          <div className="grid max-h-80 gap-2 overflow-auto">
            {merged.map((item) => (
              <div key={item.id} className="rounded-lg border border-divider bg-surface px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-ink">{item.label.replace(/[._]/g, " ")}</span>
                  <span className="text-[11px] text-muted">{new Date(item.when).toLocaleString()}</span>
                </div>
                <div className="truncate text-xs text-secondary">{item.detail}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ActionModal>
  );
}

function UserEditor({ user, defaultFirmId, firms, onUploadPhoto, onClose, onSave }: { user?: User; defaultFirmId?: string; firms: Firm[]; onUploadPhoto?: (file: File) => Promise<string>; onClose: () => void; onSave: (user: User) => void | Promise<void> }) {
  const [draft, setDraft] = useState<User>(() => {
    const normalized = normalizeUserDraft(user ?? {
      id: `local-user-${Date.now()}`,
      name: "",
      email: "",
      firmId: defaultFirmId ?? firms[0]?.id ?? null,
      role: "Full Member Representative",
      memberCategory: "full_member_representative",
      roles: [],
      representativeType: "participant",
      avatarUrl: null,
      active: true,
      notificationPreference: "daily digest",
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    return {
      ...normalized,
      firstName: normalized.firstName ?? (normalized.name ? normalized.name.split(" ")[0] : ""),
      lastName: normalized.lastName ?? (normalized.name ? normalized.name.split(" ").slice(1).join(" ") : ""),
    };
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPhotoSelected = (file: File | null) => {
    setPhotoFile(file);
    if (!file) {
      setPhotoPreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  };

  const saveUser = async () => {
    const firstName = (draft.firstName ?? "").trim();
    const lastName = (draft.lastName ?? "").trim();
    const name = [firstName, lastName].filter(Boolean).join(" ").trim() || draft.name.trim();
    const email = draft.email.trim().toLowerCase();
    if (!name) {
      setError("A first name or surname is required.");
      return;
    }
    if (!email) {
      setError("Email is required.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      let avatarStorageId = draft.avatarStorageId ?? undefined;
      if (photoFile && onUploadPhoto) avatarStorageId = await onUploadPhoto(photoFile);
      await onSave({ ...draft, role: legacyRoleForUserAccess(draft.memberCategory ?? memberCategoryForUser(draft), draft.roles ?? []), name, firstName: firstName || null, lastName: lastName || null, email, avatarStorageId, updatedAt: Date.now() });
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const previewUrl = photoPreview ?? draft.avatarUrl ?? null;
  return (
    <ActionModal title={user ? "Edit user" : "Add user"} description={draft.firmId ? firms.find((firm) => firm.id === draft.firmId)?.name : "No firm selected"} confirmLabel={saving ? "Saving..." : "Save user"} onClose={onClose} onConfirm={saving ? undefined : saveUser}>
      <div className="grid gap-3">
        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="First name" value={draft.firstName ?? ""} onChange={(value) => setDraft((current) => ({ ...current, firstName: value }))} />
          <Field label="Surname" value={draft.lastName ?? ""} onChange={(value) => setDraft((current) => ({ ...current, lastName: value }))} />
        </div>
        <Field label="Email" value={draft.email} onChange={(value) => setDraft((current) => ({ ...current, email: value }))} />
        <div>
          <span className="mb-1 block text-xs font-medium text-muted">Member photo</span>
          <div className="flex items-center gap-3">
            <Avatar name={[draft.firstName, draft.lastName].filter(Boolean).join(" ") || draft.name || "?"} imageUrl={previewUrl} />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(event) => onPhotoSelected(event.target.files?.[0] ?? null)} />
            <Button type="button" variant="secondary" onClick={() => fileRef.current?.click()} disabled={!onUploadPhoto}>{photoFile ? `Selected: ${photoFile.name}` : "Upload photo"}</Button>
          </div>
          {!onUploadPhoto ? <p className="mt-1 text-[11px] text-muted">Direct photo upload is available in the live workspace.</p> : null}
        </div>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Firm</span>
          <select className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={draft.firmId ?? ""} onChange={(event) => setDraft((current) => ({ ...current, firmId: event.target.value || null }))}>
            <option value="">No firm</option>
            {firms.map((firm) => <option key={firm.id} value={firm.id}>{firm.name}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Member category</span>
          <select className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={draft.memberCategory ?? memberCategoryForUser(draft)} onChange={(event) => setDraft((current) => ({ ...current, memberCategory: event.target.value as UserMemberCategory }))}>
            {userMemberCategories.map((category) => <option key={category} value={category}>{memberCategoryLabel(category)}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Roles</span>
          <div className="grid gap-2 rounded-lg border border-line bg-surface p-3">
            {technicalRoles.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm text-secondary">
                <input type="checkbox" checked={(draft.roles ?? []).includes(role)} onChange={() => setDraft((current) => ({ ...current, roles: toggleTechnicalRole(current.roles ?? [], role) }))} />
                <span>{technicalRoleLabel(role)}</span>
              </label>
            ))}
          </div>
        </label>
        <Select label="Representative type" values={representativeTypes} value={draft.representativeType} onChange={(value) => setDraft((current) => ({ ...current, representativeType: value as RepresentativeType }))} />
        <label className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm">
          <input type="checkbox" checked={draft.active} onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))} />
          Active user
        </label>
        <p className="text-xs leading-5 text-muted">
          This creates or updates the member record in Convex. The person also needs an authentication account with the same email address before they can sign in.
        </p>
      </div>
    </ActionModal>
  );
}

function normalizeUserDraft(user: User): User {
  return {
    ...user,
    memberCategory: user.memberCategory ?? memberCategoryForUser(user),
    roles: user.roles ?? [],
  };
}

function toggleTechnicalRole(current: TechnicalRole[], role: TechnicalRole) {
  return current.includes(role) ? current.filter((item) => item !== role) : [...current, role];
}

function legacyRoleForUserAccess(memberCategory: UserMemberCategory, roles: TechnicalRole[]): Role {
  if (roles.includes("superadministrator")) return "Super Admin";
  if (roles.includes("administrator")) return "Governance Admin";
  if (roles.includes("workspace_manager")) return "Coordination Committee";
  if (memberCategory === "full_member_representative") return "Full Member Representative";
  if (memberCategory === "alternate_representative") return "Alternate Representative";
  if (memberCategory === "associate_member") return "Associate Member";
  if (memberCategory === "contributor") return "Expert Contributor";
  return "Observer";
}

function roleSummary(user: User) {
  const category = memberCategoryLabel(user.memberCategory ?? memberCategoryForUser(user));
  const labels = (user.roles ?? []).map(technicalRoleLabel);
  return labels.length > 0 ? `${category} · ${labels.join(", ")}` : category;
}

function MembershipCategoryEditor({ config, onClose, onSave }: { config: MembershipCategoryConfig; onClose: () => void; onSave: (config: MembershipCategoryConfig) => void }) {
  const [draft, setDraft] = useState(config);
  return (
    <ActionModal title={`Edit ${config.category}`} description="Explain what this category means for access, voting and member participation." confirmLabel="Save category" onClose={onClose} onConfirm={() => onSave(draft)}>
      <div className="grid gap-3">
        <Field label="Category" value={draft.category} onChange={() => undefined} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">What this category means</span>
          <textarea className="min-h-24 w-full rounded-lg border border-line bg-surface px-3 py-2" value={draft.explanation} onChange={(event) => setDraft((current) => ({ ...current, explanation: event.target.value }))} />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Access summary</span>
          <textarea className="min-h-24 w-full rounded-lg border border-line bg-surface px-3 py-2" value={draft.accessSummary} onChange={(event) => setDraft((current) => ({ ...current, accessSummary: event.target.value }))} />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Voting summary</span>
          <textarea className="min-h-24 w-full rounded-lg border border-line bg-surface px-3 py-2" value={draft.votingSummary} onChange={(event) => setDraft((current) => ({ ...current, votingSummary: event.target.value }))} />
        </label>
        <label className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm">
          <input type="checkbox" checked={draft.active} onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))} />
          Category is active
        </label>
      </div>
    </ActionModal>
  );
}

function PracticeAreaEditor({ area, onClose, onSave }: { area?: LexData["practiceAreas"][number]; onClose: () => void; onSave: (area: LexData["practiceAreas"][number]) => void | Promise<void> }) {
  const [name, setName] = useState(area?.name ?? "");
  const [slug, setSlug] = useState(area?.slug ?? "");
  const [description, setDescription] = useState(area?.description ?? "");
  const [active, setActive] = useState(area?.active ?? true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const resolvedSlug = (slug || name).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const saveArea = async () => {
    if (!name.trim()) {
      setError("Practice area name is required.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({
        id: area?.id ?? `local-practice-${Date.now()}`,
        name: name.trim(),
        slug: resolvedSlug,
        description: description.trim(),
        active,
        createdBy: area?.createdBy ?? "user-1",
        createdAt: area?.createdAt ?? Date.now(),
        updatedAt: Date.now(),
      });
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ActionModal
      title={area ? "Edit practice area" : "Add practice area"}
      description="Create or update a global practice area that can be linked to firms, members and recommendations."
      confirmLabel={saving ? "Saving..." : "Save practice area"}
      onClose={onClose}
      onConfirm={saving ? undefined : saveArea}
    >
      <div className="grid gap-3">
        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div> : null}
        <Field label="Practice area name" value={name} onChange={setName} />
        <Field label="Slug" value={slug} onChange={setSlug} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Description</span>
          <textarea
            className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Describe when this practice area should be used."
          />
        </label>
        <label className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm">
          <input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} />
          Practice area is active
        </label>
      </div>
    </ActionModal>
  );
}

function AffiliationGroupEditor({ group, onClose, onSave }: { group?: AffiliationGroup; onClose: () => void; onSave: (group: AffiliationGroup) => void }) {
  const [draft, setDraft] = useState<AffiliationGroup>(group ?? {
    id: `local-affiliation-${Date.now()}`,
    name: "",
    affiliationType: "formal alliance",
    explanation: "",
    active: true,
    createdBy: "user-1",
    createdAt: Date.now(),
    updatedAt: Date.now(),
  });
  return (
    <ActionModal title={group ? "Edit affiliation group" : "Create affiliation group"} description="Record structural relationships without exposing them as member-facing trust signals." confirmLabel="Save group" onClose={onClose} onConfirm={() => onSave({ ...draft, updatedAt: Date.now() })}>
      <div className="grid gap-3">
        <Field label="Group name" value={draft.name} onChange={(value) => setDraft((current) => ({ ...current, name: value }))} />
        <Select label="Affiliation type" values={affiliationTypes} value={draft.affiliationType} onChange={(value) => setDraft((current) => ({ ...current, affiliationType: value }))} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Short explanation</span>
          <textarea
            maxLength={300}
            className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2"
            value={draft.explanation}
            onChange={(event) => setDraft((current) => ({ ...current, explanation: event.target.value }))}
            placeholder="Explain the structural relationship in no more than 300 characters."
          />
          <span className="mt-1 block text-xs text-muted">{draft.explanation.length}/300 characters</span>
        </label>
        <label className="flex min-h-11 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-sm">
          <input type="checkbox" checked={draft.active} onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))} />
          Affiliation group is active
        </label>
      </div>
    </ActionModal>
  );
}

function TopicTagEditor({ onClose, onSave }: { onClose: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState("");
  return (
    <ActionModal title="Suggest topic tag" description="Suggested tags require admin approval before they become global topic tags." confirmLabel="Save suggestion" onClose={onClose} onConfirm={() => onSave(name)}>
      <Field label="Topic tag name" value={name} onChange={setName} />
      <p className="mt-3 text-sm leading-6 text-secondary">Use a substantive subject label, not a post type or jurisdiction. For example, Data protection is a topic; Question is a post type; EU is a jurisdiction.</p>
    </ActionModal>
  );
}

function JurisdictionTagEditor({ onClose, onSave }: { onClose: () => void; onSave: (name: string) => void | Promise<void> }) {
  const [name, setName] = useState("");
  return (
    <ActionModal title="Create jurisdiction tag" description="Add a jurisdiction or institution label that can be used across public and member-facing content." confirmLabel="Save jurisdiction" onClose={onClose} onConfirm={() => onSave(name)}>
      <Field label="Jurisdiction or institution name" value={name} onChange={setName} />
    </ActionModal>
  );
}

function TopicMergeEditor({ sourceName, topicTags, onClose, onSave }: { sourceName: string; topicTags: string[]; onClose: () => void; onSave: (targetName: string) => void | Promise<void> }) {
  const targets = topicTags.filter((tag) => tag !== sourceName);
  const [targetName, setTargetName] = useState(targets[0] ?? "");
  return (
    <ActionModal title="Merge topic tag" description={`Choose the canonical tag that should replace ${sourceName}.`} confirmLabel="Merge tag" onClose={onClose} onConfirm={() => onSave(targetName)}>
      <div className="grid gap-3">
        <Field label="Source tag" value={sourceName} onChange={() => undefined} />
        <Select label="Canonical target" values={targets} value={targetName} onChange={setTargetName} />
        <p className="text-sm leading-6 text-secondary">The source tag will be archived and the audit log will record the canonical target.</p>
      </div>
    </ActionModal>
  );
}

function AuditDetailModal({ record, onClose }: { record: AuditRecord; onClose: () => void }) {
  return (
    <ActionModal title="Audit detail" description={`${record.actionType} on ${record.entityType}`} confirmLabel="Close" onClose={onClose} onConfirm={onClose}>
      <div className="grid gap-3 text-sm">
        <InfoLine label="Actor" value={record.actor ?? "System"} />
        <InfoLine label="Entity" value={`${record.entityType} · ${record.entityId}`} />
        <InfoLine label="Date" value={new Date(record.createdAt).toLocaleString()} />
        {record.beforeJson ? (
          <label>
            <span className="mb-1 block text-xs font-medium text-muted">Before</span>
            <pre className="max-h-44 overflow-auto rounded-lg border border-line bg-canvas p-3 text-xs text-secondary">{record.beforeJson}</pre>
          </label>
        ) : null}
        {record.afterJson ? (
          <label>
            <span className="mb-1 block text-xs font-medium text-muted">After</span>
            <pre className="max-h-52 overflow-auto rounded-lg border border-line bg-canvas p-3 text-xs text-secondary">{record.afterJson}</pre>
          </label>
        ) : null}
      </div>
    </ActionModal>
  );
}

function SectionActionModal({ modal, onClose, onConfirm }: { modal: Extract<AdminModal, { kind: "health" }>; onClose: () => void; onConfirm: (input: { title: string; notes: string }) => void | Promise<void> }) {
  const [title, setTitle] = useState(modal.title ?? "Admin action");
  const [notes, setNotes] = useState("");
  return (
    <ActionModal title={modal.title ?? "Admin action"} description="Save this administrative update so the workspace records who changed it and why." confirmLabel="Save" onClose={onClose} onConfirm={() => onConfirm({ title: title.trim() || "Admin action", notes })}>
      <div className="grid gap-3">
        <Field label="Name" value={title} onChange={setTitle} />
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Notes</span>
          <textarea className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Add an admin note for this change." />
        </label>
      </div>
    </ActionModal>
  );
}

function SimplePanel({ title, body, rows, actionLabel, onAction }: { title: string; body: string; rows: string[]; actionLabel?: string; onAction?: () => void }) {
  return (
    <Section title={title} aside={actionLabel && onAction ? <Button type="button" variant="secondary" onClick={onAction}>{actionLabel}</Button> : null}>
      <Card>
        <p className="text-sm text-secondary">{body}</p>
        <div className="mt-4 grid gap-2">
          {rows.map((row) => <div key={row} className="rounded-lg border border-divider bg-canvas px-3 py-2 text-sm">{row}</div>)}
        </div>
      </Card>
    </Section>
  );
}

function NumberField({ label, value, onChange, step = "1" }: { label: string; value: number; onChange: (value: number) => void; step?: string }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      <input type="number" step={step} className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-divider bg-canvas p-3">
      <div className="text-2xl font-semibold tracking-[-0.02em]">{value}</div>
      <div className="mono mt-1 text-[10px] uppercase tracking-[0.13em] text-muted">{label}</div>
    </div>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-divider bg-canvas px-3 py-2">
      <div className="mono text-[10px] uppercase tracking-[0.13em] text-muted">{label}</div>
      <div className="mt-1 text-secondary">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      <input type={type} className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Select({ label, values, value, onChange }: { label: string; values: string[]; value?: string; onChange?: (value: string) => void }) {
  return (
    <label>
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      <select className="min-h-11 w-full rounded-md border border-line bg-elevated px-3" value={value} onChange={(event) => onChange?.(event.target.value)}>
        {values.map((item) => <option key={item}>{item}</option>)}
      </select>
    </label>
  );
}

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown error.";
}

function emailNotice(result: InvitationEmailResult | undefined, sentMessage: string, fallbackMessage: string) {
  if (!result) return fallbackMessage;
  if (result.emailSent) return sentMessage;
  return `${fallbackMessage} ${result.emailError ?? "Check RESEND_API_KEY, CLERK_SECRET_KEY, EMAIL_FROM and LEXMOSAIC_APP_URL."}`;
}

function latestEmailByUserId(emailOutbox: AdminEmailOutboxRecord[]) {
  const map = new Map<string, AdminEmailOutboxRecord>();
  for (const email of emailOutbox.filter((item) => item.targetType === "userInvitation")) {
    const current = map.get(email.userId);
    if (!current || email.updatedAt > current.updatedAt) map.set(email.userId, email);
  }
  return map;
}

function practiceDefinitionToArea(definition: PracticeAreaDefinition): LexData["practiceAreas"][number] {
  const now = Date.now();
  return {
    id: `local-practice-${definition.slug}`,
    name: definition.name,
    slug: definition.slug,
    description: definition.description,
    active: true,
    createdBy: "admin",
    createdAt: now,
    updatedAt: now,
  };
}

function downloadTextFile(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
